
import { ensureCompanyBootstrap } from './core/company/companyBootstrap';
import {
  resolveCompaniesCollectionPath,
  resolveCompanyDocPath,
  resolveCompanyGeneralSettingsPath,
  resolveCompanyMainSettingsPath,
  resolveCompanyModulesSettingsPath,
  resolveCompanyPlanSettingsPath,
  resolveRoutesCollectionPath,
} from './core/company/companyPaths';
import React, { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import { onAuthStateChanged, signOut } from 'firebase/auth';
import {
  collection,
  onSnapshot,
  doc,
  serverTimestamp,
  setDoc,
  addDoc,
  query,
  where,
  getDoc,
  getDocs,
} from 'firebase/firestore';
import { auth, db, firebaseInitError, appId } from './firebase-config';
import StockControlApp from './StockControl';
import LogisticsApp from './Logistics';
import ServiceApp from './ServiceApp';
import RouteMonitor from './RouteMonitor';
import Reports from './Reports';
import Auth from './Auth';
import PublicLanding from './PublicLanding';
import TrialSignupPage from './TrialSignupPage';
import DemoRequestPage from './DemoRequestPage';
import SettingsModal from './SettingsModal';
import CompanySettingsModal from './CompanySettingsModal';
import * as Tone from 'tone';
import packageJson from '../package.json';
import Dashboard from './Dashboard';
import FleetExpensesApp from './FleetExpenses';
import SystemGuide from './SystemGuide';
import TransporterLogisticsApp from './TransporterLogistics';
import LogisticsDashboard from './LogisticsDashboard';
import ArchitectPanel from './ArchitectPanel';
import OperationalDatabaseView from './OperationalDatabaseView';
import StockControllerAccessPage from './StockControllerAccessPage';
import { getCompanyAccessKeyFromPath, sanitizeCompanyAccessSlug } from './core/company/companyAccess';
import { resolveCommercialState, resolveModulesSettingsWithPlanFallback } from './companyPlans';
import TutorialProvider from './core/tutorial/TutorialProvider';
import useTutorial from './core/tutorial/useTutorial';
import TutorialOverlay from './core/tutorial/components/TutorialOverlay';
import {
  buildControlUserProfile,
  canAccessControlPanel,
  isControlAccessEmail,
} from './core/security/controlAccess';
import { buildGiaContextPacket, buildGiaQuickAnswers, getGiaFlowKnowledge } from './core/gia/giaKnowledge';
import gcentraHeaderMap from './assets/gcentra-header-routes-wide-v3.png';
import gcentraMarkCutout from './assets/gcentra-mark-clean.png';
import { trackPublicPageVisit } from './publicTrafficTelemetry';

const DEFAULT_COMPANY_ID = 'default';

const getRouteMergeKey = (route = {}) =>
  String(route?.legacySourceId || route?.id || '').trim() ||
  [
    String(route?.date || '').trim(),
    String(route?.transporterId || route?.transporterSlug || route?.transportType || route?.transport || '').trim(),
    String(route?.name || '').trim()
  ].join('::');

const getRouteUpdatedAtMillis = (route = {}) => {
  const updatedAt = route?.updatedAt;
  if (updatedAt?.toDate) return updatedAt.toDate().getTime();
  if (updatedAt instanceof Date) return updatedAt.getTime();

  const createdAt = route?.createdAt;
  if (createdAt?.toDate) return createdAt.toDate().getTime();
  if (createdAt instanceof Date) return createdAt.getTime();

  return 0;
};

const mergeLogisticsRoutes = (companyRoutes = [], legacyRoutes = []) => {
  const mergedByKey = new Map();

  [...companyRoutes, ...legacyRoutes].forEach((route) => {
    const key = getRouteMergeKey(route);
    if (!key) return;

    const current = mergedByKey.get(key);
    if (!current) {
      mergedByKey.set(key, route);
      return;
    }

    const currentMillis = getRouteUpdatedAtMillis(current);
    const nextMillis = getRouteUpdatedAtMillis(route);

    if (nextMillis >= currentMillis) {
      mergedByKey.set(key, {
        ...current,
        ...route,
        legacySourceId: route.legacySourceId || current.legacySourceId || current.id || route.id
      });
      return;
    }

    mergedByKey.set(key, {
      ...route,
      ...current,
      legacySourceId: current.legacySourceId || route.legacySourceId || current.id || route.id
    });
  });

  return Array.from(mergedByKey.values());
};
const DEFAULT_COMPANY_NAME = 'Empresa Base';
const CONTROL_PATH_REGEX = /^\/(?:control|backoffice|arquitectura)\/?$/i;
const LOGIN_PATH_REGEX = /^\/ingresar\/?$/i;
const TRIAL_PATH_REGEX = /^\/(?:trial|probar-gratis)\/?$/i;
const PLANS_PATH_REGEX = /^\/planes\/?$/i;
const COMMERCIAL_REQUEST_PATH_REGEX = /^\/(?:demo|solicitar-plan)\/?$/i;
const STOCK_CONTROLLER_PATH_REGEX = /^\/(?:empresa|empresas|company|companies|c)\/([^/?#]+)\/control\/([^/?#]+)\/?$/i;

const getStockControllerAccessFromPath = (pathname = '') => {
  const path = String(pathname || '').trim();
  const match = path.match(STOCK_CONTROLLER_PATH_REGEX);
  if (!match?.[1] || !match?.[2]) return null;
  try {
    return {
      companyAccessKey: decodeURIComponent(match[1]),
      controllerAccessKey: decodeURIComponent(match[2]),
    };
  } catch (error) {
    return {
      companyAccessKey: match[1],
      controllerAccessKey: match[2],
    };
  }
};

const detectAppPathMode = (pathname = '') => {
  const path = String(pathname || '').trim() || '/';

  if (getStockControllerAccessFromPath(path)) return 'stock-controller';
  if (/^\/transporter\//i.test(path) || /^\/empresa\/transporter\//i.test(path)) return 'transporter';
  if (getCompanyAccessKeyFromPath(path)) return 'company';
  if (CONTROL_PATH_REGEX.test(path)) return 'control';
  if (LOGIN_PATH_REGEX.test(path)) return 'login';
  if (TRIAL_PATH_REGEX.test(path)) return 'trial';
  if (COMMERCIAL_REQUEST_PATH_REGEX.test(path)) return 'commercial-request';
  if (PLANS_PATH_REGEX.test(path)) return 'plans';
  return 'public';
};

const isPermissionDeniedError = (error) => {
  return (
    error?.code === 'permission-denied' ||
    String(error?.message || '').toLowerCase().includes('missing or insufficient permissions')
  );
};

const normalizeCompanyOption = (entry) => {
  if (!entry) return null;

  if (typeof entry === 'string') {
    return {
      id: entry,
      name: entry === DEFAULT_COMPANY_ID ? DEFAULT_COMPANY_NAME : entry,
      accessSlug: '',
      accessPath: '',
    };
  }

  if (typeof entry === 'object') {
    const id = entry.id || entry.companyId || entry.slug || entry.value;
    if (!id) return null;

    return {
      id,
      name:
        entry.name ||
        entry.companyName ||
        entry.label ||
        (id === DEFAULT_COMPANY_ID ? DEFAULT_COMPANY_NAME : id),
      accessSlug: entry.accessSlug || entry.companySlug || entry.slug || '',
      accessPath: entry.accessPath || '',
    };
  }

  return null;
};

const NAV_MODULES = [
  {
    viewKey: 'logistics',
    moduleKey: 'logistics',
    label: 'Logística',
    icon: 'M8.25 18.75a1.5 1.5 0 01-3 0m3 0a1.5 1.5 0 00-3 0m3 0h6m-9 0H3.375a1.125 1.125 0 01-1.125-1.125V14.25m17.25 4.5a1.5 1.5 0 01-3 0m3 0a1.5 1.5 0 00-3 0m3 0h1.125c.621 0 1.129-.504 1.09-1.124a17.902 17.902 0 00-3.213-9.193 2.056 2.056 0 00-1.58-.86H14.25',
    color: 'text-emerald-400',
    activeGlow: 'shadow-[0_0_15px_3px_rgba(16,185,129,0.5)]',
  },
  {
    viewKey: 'service',
    moduleKey: 'service',
    label: 'Servicio',
    icon: 'M3.75 6.75h16.5M6.75 3.75v6m10.5-6v6M4.5 10.5h15A2.25 2.25 0 0121.75 12.75v5.25A2.25 2.25 0 0119.5 20.25h-15A2.25 2.25 0 012.25 18v-5.25A2.25 2.25 0 014.5 10.5z',
    color: 'text-cyan-400',
    activeGlow: 'shadow-[0_0_15px_3px_rgba(34,211,238,0.5)]',
  },
  {
    viewKey: 'monitor',
    moduleKey: 'routeMonitor',
    label: 'Monitor en tiempo real',
    icon: 'M9 17.25v.001M12 17.25v.001M15 17.25v.001M4.5 5.25v13.5a2.25 2.25 0 002.25 2.25h10.5a2.25 2.25 0 002.25-2.25V5.25a2.25 2.25 0 00-2.25-2.25H6.75a2.25 2.25 0 00-2.25 2.25z',
    color: 'text-indigo-400',
    activeGlow: 'shadow-[0_0_15px_3px_rgba(129,140,248,0.5)]',
  },
  {
    viewKey: 'reports',
    moduleKey: 'reports',
    label: 'Reportes y liquidación',
    icon: 'M3 3.75A.75.75 0 013.75 3h10.5a.75.75 0 01.53.22l3 3a.75.75 0 01.22.53v13.5a.75.75 0 01-.75.75H3.75a.75.75 0 01-.75-.75V3.75zm3.75 4.5h6m-6 3h10.5m-10.5 3h10.5m-10.5 3h6',
    color: 'text-fuchsia-400',
    activeGlow: 'shadow-[0_0_15px_3px_rgba(217,70,239,0.45)]',
  },
  {
    viewKey: 'fleetExpenses',
    moduleKey: 'fleetExpenses',
    label: 'Gastos de Flota',
    icon: 'M2.25 8.25h19.5M2.25 9h19.5m-16.5 5.25h6m-6 2.25h6m3-3.75l-3.75-3.75A1.125 1.125 0 015.25 6H3.75a2.25 2.25 0 00-2.25 2.25v1.5a2.25 2.25 0 002.25 2.25h1.5a1.125 1.125 0 01.83.375l3.75 3.75a1.125 1.125 0 010 1.59l-3.75 3.75a1.125 1.125 0 01-.83.375H3.75a2.25 2.25 0 01-2.25-2.25v-1.5a2.25 2.25 0 012.25-2.25h1.5A1.125 1.125 0 005.25 12z',
    color: 'text-amber-400',
    activeGlow: 'shadow-[0_0_15px_3px_rgba(251,191,36,0.5)]',
  },
  {
    viewKey: 'stock',
    moduleKey: 'stock',
    label: 'Stock y movimientos',
    icon: 'M3.75 21h16.5M4.5 3h15M5.25 3v18m13.5-18v18M9 6.75h6.375a.375.375 0 01.375.375v1.5a.375.375 0 01-.375.375H9a.375.375 0 01-.375-.375v-1.5A.375.375 0 019 6.75z',
    color: 'text-sky-400',
    activeGlow: 'shadow-[0_0_15px_3px_rgba(56,189,248,0.5)]',
  },
  {
    viewKey: 'systemGuide',
    moduleKey: 'systemGuide',
    label: 'Guía y soporte',
    icon: 'M11.25 11.25l.041-.02a.75.75 0 011.063.852l-.708 2.836a.75.75 0 001.063.853l.041-.021M21 12a9 9 0 11-18 0 9 9 0 0118 0zm-9-3.75h.008v.008H12V8.25z',
    color: 'text-rose-400',
    activeGlow: 'shadow-[0_0_15px_3px_rgba(251,113,133,0.5)]',
  },
];

const getEnabledModulesCount = (modulesSettings = null) => {
  return NAV_MODULES.filter(({ moduleKey }) => modulesSettings?.[moduleKey]?.enabled !== false).length;
};

const Icon = ({ path, className = 'w-6 h-6', ...props }) => (
  <svg
    xmlns="http://www.w3.org/2000/svg"
    fill="none"
    viewBox="0 0 24 24"
    strokeWidth={1.5}
    stroke="currentColor"
    className={className}
    {...props}
  >
    <path strokeLinecap="round" strokeLinejoin="round" d={path} />
  </svg>
);


const prettifyAccessLabel = (value = '') => {
  const raw = String(value || '').trim();
  if (!raw) return '';
  return raw
    .replace(/[-_]+/g, ' ')
    .split(' ')
    .filter(Boolean)
    .map((part) => part.charAt(0).toUpperCase() + part.slice(1))
    .join(' ');
};

const buildCompanyScopedFallbackUser = ({
  currentUser,
  companyId,
  companyName,
  accessSlug = '',
}) => {
  const resolvedCompanyName =
    companyName || (companyId === DEFAULT_COMPANY_ID ? DEFAULT_COMPANY_NAME : companyId) || DEFAULT_COMPANY_NAME;
  const normalizedEmail = String(currentUser?.email || '').trim().toLowerCase();
  const resolvedAccessSlug =
    String(accessSlug || '').trim() || sanitizeCompanyAccessSlug(resolvedCompanyName, companyId || 'empresa');

  return {
    uid: currentUser?.uid,
    email: normalizedEmail,
    role: 'companyAdmin',
    accessScope: 'company',
    companyId,
    companyIds: [companyId],
    companyName: resolvedCompanyName,
    accessSlug: resolvedAccessSlug,
    companySlug: resolvedAccessSlug,
    allowedCompanies: [{ id: companyId, name: resolvedCompanyName }],
    availableCompanies: [{ id: companyId, name: resolvedCompanyName }],
    active: true,
  };
};

const derivePrimaryCompanyOptionFromUserData = (userData = null) => {
  if (!userData || typeof userData !== 'object') return null;

  const fallbackCandidates = [
    ...(Array.isArray(userData.allowedCompanies) ? userData.allowedCompanies : []),
    ...(Array.isArray(userData.availableCompanies) ? userData.availableCompanies : []),
    ...(Array.isArray(userData.companies) ? userData.companies : []),
  ]
    .map((option) => normalizeCompanyOption(option))
    .filter(Boolean);

  if (fallbackCandidates.length > 0) return fallbackCandidates[0];

  if (userData.companyId && userData.companyId !== DEFAULT_COMPANY_ID) {
    return normalizeCompanyOption({
      id: userData.companyId,
      name: userData.companyName || userData.companyId,
      accessSlug: userData.accessSlug || userData.companySlug || '',
    });
  }

  return null;
};

const SplashScreen = ({ eyebrow = '', title = 'G-CENTRA', secondaryTitle = 'El centro de tu logística, en tiempo real', description = 'Monitoreá rutas, transportistas y entregas desde una sola plataforma.' }) => (
  <div className="flex min-h-screen flex-col items-center justify-center bg-slate-950 px-4 text-white animate-fade-in">
    <div className="text-center">
      {eyebrow ? <p className="text-xs font-semibold uppercase tracking-[0.25em] text-cyan-300">{eyebrow}</p> : null}
      <h1 className="mt-2 text-5xl font-black tracking-tight md:text-7xl">{title}</h1>
      {secondaryTitle ? <p className="mt-4 text-2xl font-bold tracking-tight text-white md:text-4xl">{secondaryTitle}</p> : null}
      <p className="mt-4 text-sm text-slate-400 md:text-base">{description}</p>
    </div>
  </div>
);

const ErrorScreen = ({ error }) => (
  <div className="flex min-h-screen items-center justify-center bg-red-100 p-4">
    <div className="rounded-lg bg-white p-6 text-center shadow-lg">
      <h2 className="mb-4 text-2xl font-bold text-red-700">Error crítico</h2>
      <p className="text-red-600">{error}</p>
    </div>
  </div>
);

const GIA_FLOW_BY_VIEW = {
  menu: 'outboundOrder',
  logistics: 'routeLifecycle',
  stock: 'purchaseToStock',
  monitor: 'routeLifecycle',
  reports: 'physicalControl',
  fleetExpenses: null,
  systemGuide: null,
  architect: null,
};

const resolveGiaSubsection = ({
  view,
  isArchitectView,
  selectedTransport,
  dashboardPlansOpen,
}) => {
  if (isArchitectView) {
    return { subsectionLabel: 'Arquitectura', focusLabel: '' };
  }

  if (view === 'menu') {
    return dashboardPlansOpen
      ? { subsectionLabel: 'Planes y continuidad', focusLabel: '' }
      : { subsectionLabel: 'Panel principal', focusLabel: '' };
  }

  if (view === 'logistics') {
    if (selectedTransport?.name) {
      return {
        subsectionLabel: 'Unidad individual',
        focusLabel: selectedTransport.name,
      };
    }

    return { subsectionLabel: 'Choferes y unidades', focusLabel: '' };
  }

  if (view === 'service') {
    return { subsectionLabel: 'Pedidos y contratos', focusLabel: '' };
  }

  if (view === 'monitor') {
    return { subsectionLabel: 'Monitor en tiempo real', focusLabel: '' };
  }

  if (view === 'stock') {
    return { subsectionLabel: 'Inventario y salidas', focusLabel: '' };
  }

  if (view === 'reports') {
    return { subsectionLabel: 'Balance e historial', focusLabel: '' };
  }

  if (view === 'fleetExpenses') {
    return { subsectionLabel: 'Costos de flota', focusLabel: '' };
  }

  if (view === 'systemGuide') {
    return { subsectionLabel: 'Acompañamiento guiado', focusLabel: '' };
  }

  return { subsectionLabel: '', focusLabel: '' };
};

const GIA_PANEL_TABS = {
  summary: 'Resumen',
  actions: 'Qué podés hacer',
  next: 'Primer paso',
  issues: 'Errores comunes',
};

const GIA_ACTION_THEME = {
  primary: 'border-cyan-400/25 bg-cyan-500/10 text-cyan-100 hover:bg-cyan-500/18',
  secondary: 'border-slate-700/85 bg-slate-900/95 text-slate-200 hover:bg-slate-800/95',
};

const GiaAssistantPanel = ({
  open,
  onClose,
  contextPacket,
  quickAnswers,
  flowKnowledge,
  onOpenGuide,
  actions = [],
}) => {
  const [activeTab, setActiveTab] = useState('summary');

  useEffect(() => {
    if (!open) return;
    setActiveTab('summary');
  }, [open, contextPacket?.sectionLabel]);

  if (!open) return null;

  const tabContent = {
    summary: (
      <div className="space-y-3">
        <p className="text-sm leading-6 text-slate-200">{quickAnswers.summary}</p>
        <div className="rounded-2xl border border-emerald-400/20 bg-emerald-500/12 p-3">
          <p className="text-[11px] font-black uppercase tracking-[0.18em] text-emerald-300">Propósito</p>
          <p className="mt-1 text-sm leading-6 text-slate-100">{quickAnswers.purpose}</p>
        </div>
      </div>
    ),
    actions: (
      <ul className="space-y-2">
        {quickAnswers.canDo.map((item) => (
          <li key={item} className="rounded-2xl border border-slate-700/85 bg-slate-900/95 px-3 py-2 text-sm leading-6 text-slate-100">
            {item}
          </li>
        ))}
      </ul>
    ),
    next: (
      <div className="space-y-3">
        <p className="rounded-2xl border border-cyan-400/25 bg-cyan-500/14 px-3 py-3 text-sm leading-6 text-cyan-50">
          {quickAnswers.nextStepHint}
        </p>
        {flowKnowledge ? (
          <div className="rounded-2xl border border-slate-700/85 bg-slate-900/95 p-3">
            <p className="text-[11px] font-black uppercase tracking-[0.18em] text-cyan-300">{flowKnowledge.label}</p>
            <ul className="mt-2 space-y-2">
              {flowKnowledge.steps.slice(0, 3).map((step) => (
                <li key={step} className="text-sm leading-6 text-slate-100">
                  {step}
                </li>
              ))}
            </ul>
          </div>
        ) : null}
      </div>
    ),
    issues: (
      <ul className="space-y-2">
        {quickAnswers.commonIssues.map((item) => (
          <li key={item} className="rounded-2xl border border-amber-400/20 bg-amber-500/14 px-3 py-2 text-sm leading-6 text-amber-50">
            {item}
          </li>
        ))}
      </ul>
    ),
  };

  return (
    <div className="fixed right-4 top-[92px] z-[135] w-[min(92vw,420px)] overflow-hidden rounded-[28px] border border-slate-700/90 bg-[#020817]/[0.985] shadow-[0_24px_60px_-24px_rgba(2,6,23,0.98)] backdrop-blur-md">
      <div className="border-b border-slate-800/95 bg-[linear-gradient(135deg,rgba(8,47,73,0.96),rgba(2,6,23,0.985))] px-4 py-4">
        <div className="flex items-start justify-between gap-3">
          <div className="min-w-0">
            <div className="flex items-center gap-2">
              <span className="text-[10px] font-black uppercase tracking-[0.22em] text-emerald-300">GIA</span>
              <span className="rounded-full border border-emerald-300/20 bg-emerald-500/10 px-2 py-0.5 text-[10px] font-semibold uppercase tracking-[0.14em] text-emerald-100">
                {contextPacket.sectionLabel}
              </span>
            </div>
            <p className="mt-2 text-sm leading-6 text-slate-100">{contextPacket.contextLabel}</p>
          </div>
          <button
            type="button"
            onClick={onClose}
            className="rounded-full border border-slate-700/80 bg-slate-900/80 p-2 text-slate-300 transition-colors hover:text-white"
            title="Cerrar GIA"
          >
            <Icon path="M6 18L18 6M6 6l12 12" className="h-4 w-4" />
          </button>
        </div>
      </div>

      <div className="flex flex-wrap gap-2 border-b border-slate-800/95 bg-slate-950/72 px-4 py-3">
        {Object.entries(GIA_PANEL_TABS).map(([tabKey, label]) => (
          <button
            key={tabKey}
            type="button"
            onClick={() => setActiveTab(tabKey)}
            className={`rounded-full border px-3 py-1.5 text-[11px] font-bold uppercase tracking-[0.12em] transition-all ${
              activeTab === tabKey
                ? 'border-cyan-200/70 bg-cyan-300/34 text-white shadow-[0_0_0_1px_rgba(207,250,254,0.08)]'
                : 'border-slate-700/90 bg-slate-900/96 text-slate-300 hover:text-white'
            }`}
          >
            {label}
          </button>
        ))}
      </div>

      {actions.length ? (
        <div className="flex flex-wrap gap-2 border-b border-slate-800/95 bg-slate-950/88 px-4 py-3">
          {actions.map((action) => (
            <button
              key={action.id}
              type="button"
              onClick={action.onClick}
              className={`rounded-full border px-3 py-1.5 text-[11px] font-semibold tracking-[0.02em] transition-all ${GIA_ACTION_THEME[action.tone] || GIA_ACTION_THEME.secondary}`}
            >
              {action.label}
            </button>
          ))}
        </div>
      ) : null}

      <div className="max-h-[55vh] overflow-y-auto bg-slate-950/84 px-4 py-4">
        {tabContent[activeTab]}
      </div>

      <div className="flex items-center justify-between gap-3 border-t border-slate-800/95 bg-slate-950 px-4 py-3">
        <div className="min-w-0">
          <p className="text-[11px] font-black uppercase tracking-[0.18em] text-slate-400">Modelo de soporte</p>
          <p className="mt-1 text-xs leading-5 text-slate-300">La guía acompaña primero con contexto y próximos pasos. Si no alcanza, escalamos a soporte humano para destrabar sin tocar tu operación a ciegas.</p>
        </div>
        <button
          type="button"
          onClick={onOpenGuide}
          className="shrink-0 rounded-xl border border-cyan-400/30 bg-cyan-500/10 px-3 py-2 text-xs font-semibold text-cyan-100 transition-all hover:bg-cyan-500/20"
        >
          Guía y soporte
        </button>
      </div>
    </div>
  );
};

const CompanyAccessStatusScreen = ({ title, description, badge = 'Acceso no disponible', tone = 'slate', primaryAction = null, secondaryAction = null }) => {
  const toneMap = {
    slate: {
      shell: 'bg-slate-950 text-white',
      badge: 'border-slate-700 bg-slate-900/80 text-slate-200',
      card: 'border-slate-800 bg-slate-900/80',
      title: 'text-white',
      description: 'text-slate-300',
      primary: 'border-cyan-400/30 bg-cyan-500/10 text-cyan-100 hover:bg-cyan-500/20',
      secondary: 'border-slate-700 bg-slate-900 text-slate-200 hover:bg-slate-800',
    },
    amber: {
      shell: 'bg-slate-950 text-white',
      badge: 'border-amber-400/30 bg-amber-500/10 text-amber-100',
      card: 'border-amber-500/20 bg-slate-900/85',
      title: 'text-amber-100',
      description: 'text-slate-300',
      primary: 'border-amber-400/30 bg-amber-500/10 text-amber-100 hover:bg-amber-500/20',
      secondary: 'border-slate-700 bg-slate-900 text-slate-200 hover:bg-slate-800',
    },
    red: {
      shell: 'bg-slate-950 text-white',
      badge: 'border-red-400/30 bg-red-500/10 text-red-100',
      card: 'border-red-500/20 bg-slate-900/85',
      title: 'text-red-100',
      description: 'text-slate-300',
      primary: 'border-red-400/30 bg-red-500/10 text-red-100 hover:bg-red-500/20',
      secondary: 'border-slate-700 bg-slate-900 text-slate-200 hover:bg-slate-800',
    },
  };

  const styles = toneMap[tone] || toneMap.slate;

  return (
    <div className={`flex min-h-screen items-center justify-center px-4 py-10 ${styles.shell}`}>
      <div className={`w-full max-w-2xl rounded-3xl border p-8 shadow-2xl ${styles.card}`}>
        <div className="mx-auto flex max-w-xl flex-col items-center text-center">
          <span className={`inline-flex rounded-full border px-3 py-1 text-[11px] font-semibold uppercase tracking-[0.25em] ${styles.badge}`}>
            {badge}
          </span>
          <h1 className={`mt-6 text-3xl font-black tracking-tight md:text-5xl ${styles.title}`}>{title}</h1>
          <p className={`mt-4 text-sm leading-6 md:text-base ${styles.description}`}>{description}</p>

          <div className="mt-8 flex flex-wrap items-center justify-center gap-3">
            {primaryAction ? (
              <button
                type="button"
                onClick={primaryAction.onClick}
                className={`rounded-2xl border px-5 py-3 text-sm font-semibold transition-all ${styles.primary}`}
              >
                {primaryAction.label}
              </button>
            ) : null}

            {secondaryAction ? (
              <button
                type="button"
                onClick={secondaryAction.onClick}
                className={`rounded-2xl border px-5 py-3 text-sm font-semibold transition-all ${styles.secondary}`}
              >
                {secondaryAction.label}
              </button>
            ) : null}
          </div>
        </div>
      </div>
    </div>
  );
};

const resolveCompanyAccessState = (companyDoc = {}, generalSettings = {}) => {
  const statusCandidates = [companyDoc.status, generalSettings.status]
    .map((value) => String(value || '').trim().toLowerCase())
    .filter(Boolean);

  if (companyDoc.isActive === false || companyDoc.active === false || generalSettings.isActive === false || generalSettings.active === false) {
    return 'inactive';
  }

  if (statusCandidates.includes('inactive') || statusCandidates.includes('disabled') || statusCandidates.includes('archived')) {
    return 'inactive';
  }

  return 'active';
};

const CompactInfoPill = ({ iconPath, text, tone = 'slate' }) => {
  const tones = {
    slate: 'border-slate-700 bg-slate-800/80 text-slate-200',
    cyan: 'border-cyan-400/30 bg-cyan-500/10 text-cyan-100',
    emerald: 'border-emerald-400/30 bg-emerald-500/10 text-emerald-100',
    violet: 'border-violet-400/30 bg-violet-500/10 text-violet-100',
  };

  return (
    <span className={`inline-flex items-center gap-2 rounded-full border px-3 py-1.5 text-xs font-semibold ${tones[tone] || tones.slate}`}>
      {iconPath ? <Icon path={iconPath} className="h-4 w-4" /> : null}
      <span>{text}</span>
    </span>
  );
};

const HeaderAnimation = () => {
  const canvasRef = useRef(null);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return undefined;

    const ctx = canvas.getContext('2d');
    let animationFrameId;
    let particles = [];

    const resizeCanvas = () => {
      canvas.width = canvas.offsetWidth;
      canvas.height = canvas.offsetHeight;
      particles = [];
      const particleCount = Math.floor((canvas.width * canvas.height) / 10000);
      for (let i = 0; i < particleCount; i += 1) {
        particles.push({
          x: Math.random() * canvas.width,
          y: Math.random() * canvas.height,
          vx: (Math.random() - 0.5) * 0.5,
          vy: (Math.random() - 0.5) * 0.5,
          radius: Math.random() * 1.5 + 0.5,
        });
      }
    };

    const animate = () => {
      ctx.clearRect(0, 0, canvas.width, canvas.height);

      for (let i = 0; i < particles.length; i += 1) {
        const p1 = particles[i];
        p1.x += p1.vx;
        p1.y += p1.vy;

        if (p1.x < 0 || p1.x > canvas.width) p1.vx *= -1;
        if (p1.y < 0 || p1.y > canvas.height) p1.vy *= -1;

        ctx.beginPath();
        ctx.arc(p1.x, p1.y, p1.radius, 0, Math.PI * 2);
        ctx.fillStyle = 'rgba(148, 163, 184, 0.45)';
        ctx.fill();

        for (let j = i + 1; j < particles.length; j += 1) {
          const p2 = particles[j];
          const distance = Math.hypot(p1.x - p2.x, p1.y - p2.y);
          if (distance < 120) {
            ctx.beginPath();
            ctx.moveTo(p1.x, p1.y);
            ctx.lineTo(p2.x, p2.y);
            ctx.strokeStyle = `rgba(100, 116, 139, ${1 - distance / 120})`;
            ctx.stroke();
          }
        }
      }

      animationFrameId = requestAnimationFrame(animate);
    };

    resizeCanvas();
    animate();
    window.addEventListener('resize', resizeCanvas);

    return () => {
      window.removeEventListener('resize', resizeCanvas);
      cancelAnimationFrame(animationFrameId);
    };
  }, []);

  return <canvas ref={canvasRef} className="absolute inset-0 h-full w-full" />;
};

const QuickNavSidebar = ({ activeView, onNavigate, show, enabledViews = [] }) => {
  const visibleViews = new Set(enabledViews.length ? enabledViews : ['menu', ...NAV_MODULES.map(({ viewKey }) => viewKey)]);
  const [activeLabelPulse, setActiveLabelPulse] = useState(activeView);

  useEffect(() => {
    if (!show || !activeView) return undefined;
    setActiveLabelPulse(activeView);
    const timeoutId = window.setTimeout(() => {
      setActiveLabelPulse((current) => (current === activeView ? null : current));
    }, 1000);
    return () => window.clearTimeout(timeoutId);
  }, [activeView, show]);

  const modules = [
    {
      name: 'menu',
      icon: 'M2.25 12l8.954-8.955c.44-.439 1.152-.439 1.591 0L21.75 12M4.5 9.75v10.125c0 .621.504 1.125 1.125 1.125H9.75v-4.875c0-.621.504-1.125 1.125-1.125h2.25c.621 0 1.125.504 1.125 1.125V21h4.125c.621 0 1.125-.504 1.125-1.125V9.75M8.25 21h8.25',
      label: 'Menú principal',
      color: 'text-slate-300',
      activeGlow: 'shadow-[0_0_15px_3px_rgba(203,213,225,0.5)]',
    },
    ...NAV_MODULES.map(({ viewKey, icon, label, color, activeGlow }) => ({
      name: viewKey,
      icon,
      label,
      color,
      activeGlow,
    })),
  ].filter((module) => visibleViews.has(module.name));

  return (
    <div
      id="quick-nav-sidebar"
      className={`fixed left-0 top-1/2 z-30 hidden -translate-y-1/2 flex-col gap-2 overflow-visible rounded-r-[24px] border-y border-r bg-[linear-gradient(180deg,rgba(2,6,23,0.96),rgba(15,23,42,0.94))] p-2 text-white backdrop-blur-xl transition-all duration-700 ease-[cubic-bezier(0.22,1,0.36,1)] md:flex ${
        show
          ? 'translate-x-0 border-slate-700/55 opacity-100 shadow-[0_38px_80px_rgba(2,6,23,0.46),0_12px_24px_rgba(15,23,42,0.22)]'
          : '-translate-x-[calc(100%+32px)] border-transparent opacity-0 shadow-none pointer-events-none'
      }`}
    >
      <div className="pointer-events-none absolute inset-y-4 -right-4 w-8 rounded-full bg-cyan-500/10 blur-2xl" />
      <div className="pointer-events-none absolute inset-[1px] rounded-r-[23px] bg-[linear-gradient(180deg,rgba(255,255,255,0.04),rgba(255,255,255,0.01)_18%,transparent_38%,rgba(148,163,184,0.03)_100%)]" />
      <div className="pointer-events-none absolute inset-x-0 top-0 h-10 bg-[linear-gradient(180deg,rgba(255,255,255,0.08),transparent)]" />
      <div className="pointer-events-none absolute inset-y-3 right-0 w-px bg-[linear-gradient(180deg,transparent,rgba(148,163,184,0.42),transparent)]" />
      <div className="relative mb-0.5 flex items-center justify-center px-1 py-1">
        <div className="flex h-7 w-7 items-center justify-center rounded-xl bg-cyan-500/10 text-[10px] font-black tracking-[0.24em] text-cyan-200 shadow-[0_0_18px_rgba(34,211,238,0.14)]">
          G
        </div>
      </div>
      {modules.map((mod) => {
        const isActive = activeView === mod.name;
        const showActiveAccessory = isActive && activeLabelPulse === mod.name;
        return (
          <button
            key={mod.name}
            onClick={() => onNavigate(mod.name)}
            className={`group relative z-10 flex items-center justify-center overflow-visible rounded-[18px] border px-2.5 py-2.5 transition-all duration-500 ease-[cubic-bezier(0.22,1,0.36,1)] focus:outline-none focus:ring-2 focus:ring-cyan-300 focus:ring-offset-2 focus:ring-offset-slate-950 ${
              isActive
                ? `translate-x-2.5 border-slate-500/70 bg-[linear-gradient(180deg,rgba(51,65,85,0.9),rgba(30,41,59,0.98))] shadow-[inset_0_1px_0_rgba(255,255,255,0.08),0_26px_36px_rgba(2,6,23,0.3),0_10px_12px_rgba(15,23,42,0.14)] ${mod.activeGlow}`
                : 'border-slate-700/50 bg-[linear-gradient(180deg,rgba(15,23,42,0.82),rgba(15,23,42,0.68))] hover:-translate-y-0.5 hover:border-slate-500/60 hover:bg-[linear-gradient(180deg,rgba(30,41,59,0.9),rgba(15,23,42,0.82))]'
            }`}
            title={mod.label}
          >
            <span
              className={`pointer-events-none absolute inset-y-2 -left-3 rounded-r-full bg-[linear-gradient(90deg,rgba(34,211,238,0.16),rgba(34,211,238,0.04),transparent)] blur-md transition-all duration-500 ${
                isActive ? 'w-10 opacity-100' : 'w-0 opacity-0'
              }`}
            />
            <span className={`pointer-events-none absolute right-[-7px] top-1/2 z-[-1] h-3.5 w-3.5 -translate-y-1/2 rotate-45 rounded-[4px] border-r border-b border-slate-500/60 bg-[linear-gradient(180deg,rgba(51,65,85,0.92),rgba(30,41,59,0.98))] shadow-[10px_10px_18px_rgba(2,6,23,0.18)] transition-all duration-500 ${showActiveAccessory ? 'scale-100 opacity-100' : 'scale-75 opacity-0 group-hover:scale-100 group-hover:opacity-100'}`} />
            <span className="relative flex h-10 w-10 items-center justify-center overflow-hidden rounded-[14px] bg-[radial-gradient(circle_at_30%_20%,rgba(255,255,255,0.08),transparent_46%),linear-gradient(180deg,rgba(255,255,255,0.03),rgba(15,23,42,0.14))]">
              <span className={`pointer-events-none absolute inset-y-0 -left-1/2 w-1/2 bg-[linear-gradient(90deg,transparent,rgba(255,255,255,0.18),transparent)] ${isActive ? 'animate-[moduleShine_4.4s_ease-in-out_infinite]' : 'opacity-0 group-hover:opacity-100 group-hover:animate-[moduleShine_5.6s_ease-in-out_infinite]'}`} style={{ transform: 'skewX(-18deg)' }} />
              <Icon path={mod.icon} strokeWidth={1.28} className={`relative h-5.5 w-5.5 ${mod.color} ${isActive ? 'animate-pulse' : ''}`} style={{ filter: 'drop-shadow(0 0 6px currentColor)' }} />
            </span>
            <span className={`pointer-events-none absolute left-full top-1/2 ml-2.5 h-px w-4 -translate-y-1/2 bg-[linear-gradient(90deg,rgba(34,211,238,0.72),rgba(34,211,238,0.08))] transition-all duration-500 ${showActiveAccessory ? 'opacity-100' : 'opacity-0 group-hover:opacity-100'}`} />
            <span className={`pointer-events-none absolute left-[calc(100%+1.35rem)] top-1/2 flex -translate-y-1/2 items-center gap-2 whitespace-nowrap rounded-2xl border px-3 py-1.5 text-[13px] font-semibold tracking-[-0.01em] shadow-[0_18px_36px_rgba(2,6,23,0.28)] transition-all duration-500 ${
              showActiveAccessory
                ? 'border-cyan-400/30 bg-[linear-gradient(180deg,rgba(8,47,73,0.96),rgba(15,23,42,0.94))] text-cyan-50 opacity-100'
                : 'border-slate-700/60 bg-[linear-gradient(180deg,rgba(15,23,42,0.96),rgba(15,23,42,0.9))] text-slate-100 opacity-0 group-hover:translate-x-1 group-hover:opacity-100'
            }`}>
              <span className={`h-2 w-2 rounded-full ${isActive ? 'bg-cyan-300 shadow-[0_0_10px_rgba(34,211,238,0.9)]' : 'bg-slate-500'}`} />
              {mod.label}
            </span>
          </button>
        );
      })}
    </div>
  );
};

const PasswordVerificationModal = ({
  show,
  onClose,
  user,
  onVerifySuccess,
  showMessage,
  transporterSlug = null,
  mode = 'transport',
  adminPassword = '',
}) => {
  const [password, setPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');

  const handleVerify = async (e) => {
    e.preventDefault();
    setLoading(true);
    setErrorMessage('');

    try {
      if (mode === 'admin') {
        if (!user?.uid) throw new Error('Usuario no válido para verificación admin.');

        const userRef = doc(db, 'users', user.uid);
        const userSnap = await getDoc(userRef);

        if (!userSnap.exists()) {
          setErrorMessage('No se encontró la configuración del usuario.');
          showMessage('No se encontró la configuración del usuario.', 'error');
          setLoading(false);
          return;
        }

        const userData = userSnap.data();
        const savedAdminPassword = userData?.adminPassword || adminPassword || '';

        if (savedAdminPassword && savedAdminPassword === password) {
          localStorage.setItem(`admin_pass_verified_${user.uid}`, 'true');
          onVerifySuccess();
        } else {
          setErrorMessage('Contraseña de administrador incorrecta.');
          showMessage('Contraseña de administrador incorrecta.', 'error');
        }
      } else {
        if (!transporterSlug) {
          setErrorMessage('No se definió el acceso del transporte.');
          showMessage('No se definió el acceso del transporte.', 'error');
          setLoading(false);
          return;
        }

        const transportsRef = collection(db, `artifacts/${appId}/public/data/transports`);
        const q = query(transportsRef, where('slug', '==', transporterSlug));
        const snapshot = await getDocs(q);

        if (!snapshot.empty) {
          const transportDoc = snapshot.docs[0];
          const transportData = transportDoc.data();

          if (transportData.password && transportData.password === password) {
            localStorage.setItem(`transporterAuth_${transportDoc.id}`, 'true');
            onVerifySuccess(transportDoc.id, transportData);
          } else {
            setErrorMessage('Contraseña incorrecta. Intenta de nuevo.');
            showMessage('Contraseña incorrecta. Intenta de nuevo.', 'error');
          }
        } else {
          setErrorMessage('No se encontró configuración para este transporte.');
          showMessage('No se encontró configuración para este transporte.', 'error');
        }
      }
    } catch (err) {
      console.error('Error verifying password:', err);
      setErrorMessage('Error al verificar la contraseña.');
      showMessage('Error al verificar la contraseña.', 'error');
    } finally {
      setLoading(false);
    }
  };

  if (!show) return null;

  const isAdmin = mode === 'admin';

  return (
    <div className="fixed inset-0 z-[100] flex items-center justify-center bg-black/50 p-4">
      <div className="w-full max-w-sm space-y-6 rounded-xl border border-slate-200 bg-white p-8 shadow-2xl animate-fade-in-down">
        <div className="text-center">
          <Icon path="M16.5 10.5V6a7.5 7.5 0 10-15 0v4.5m18 0l-1.5-1.5m1.5 1.5l1.5 1.5m-1.5-1.5h-12a2.25 2.25 0 00-2.25 2.25v6.75a2.25 2.25 0 002.25 2.25h12a2.25 2.25 0 002.25-2.25v-6.75a2.25 2.25 0 00-2.25-2.25z" className={`mx-auto h-16 w-16 ${isAdmin ? 'text-amber-600' : 'text-blue-600'}`} />
          <h2 className="mt-6 text-2xl font-extrabold text-slate-800">{isAdmin ? 'Acceso de administrador' : 'Acceso de chofer'}</h2>
          <p className="mt-2 text-sm text-slate-500">
            {isAdmin ? 'Ingresa la contraseña de administrador para continuar.' : 'Ingresa la contraseña para ver tu planilla del día.'}
          </p>
        </div>

        <form className="space-y-4" onSubmit={handleVerify}>
          <div className="relative">
            <input
              type={showPassword ? 'text' : 'password'}
              value={password}
              onChange={(e) => {
                setPassword(e.target.value);
                setErrorMessage('');
              }}
              placeholder="Contraseña"
              required
              className="w-full rounded-lg border border-slate-300 bg-slate-50 px-4 py-3 text-slate-700 outline-none transition-all focus:border-blue-500 focus:ring-2 focus:ring-blue-500"
            />
            <button type="button" onClick={() => setShowPassword((prev) => !prev)} className="absolute inset-y-0 right-0 pr-3" title={showPassword ? 'Ocultar contraseña' : 'Mostrar contraseña'}>
              <Icon path={showPassword ? 'M3.98 8.223A10.477 10.477 0 001.934 12C3.226 16.338 7.244 19.5 12 19.5c.993 0 1.953-.138 2.863-.395M6.228 6.228A10.451 10.451 0 0112 4.5c4.756 0 8.774 3.162 10.066 7.476-1.092 3.821-4.104 6.49-7.533 8.22M15 12a3 3 0 11-6 0 3 3 0 016 0zm-6 0a3 3 0 006 0' : 'M2.036 12.328a.9.9 0 000 1.344S4.325 17.5 12 17.5c7.675 0 9.964-3.828 9.964-3.828a.9.9 0 000-1.344S19.675 8.5 12 8.5c-7.675 0-9.964 3.828-9.964 3.828zM12 12a3 3 0 100-6 3 3 0 000 6z'} className="h-5 w-5 text-slate-400" />
            </button>
          </div>

          {errorMessage ? (
            <div className="rounded-lg bg-red-100 p-3 text-sm font-semibold text-red-700">{errorMessage}</div>
          ) : null}

          <button type="submit" disabled={loading} className={`w-full rounded-lg px-4 py-3 font-bold text-white transition-all disabled:opacity-60 ${isAdmin ? 'bg-amber-600 hover:bg-amber-700' : 'bg-blue-600 hover:bg-blue-700'}`}>
            {loading ? 'Verificando...' : 'Acceder'}
          </button>

          <button type="button" onClick={onClose} className="w-full text-slate-500 hover:text-slate-700">
            Cancelar
          </button>
        </form>
      </div>
    </div>
  );
};

function App() {
  const [currentPathname, setCurrentPathname] = useState(() => (typeof window !== 'undefined' ? window.location.pathname : '/'));
  const pathMode = useMemo(() => detectAppPathMode(currentPathname), [currentPathname]);
  const directCompanyAccessKey = useMemo(() => getCompanyAccessKeyFromPath(currentPathname), [currentPathname]);
  const stockControllerAccess = useMemo(() => getStockControllerAccessFromPath(currentPathname), [currentPathname]);
  const isStockControllerAccess = pathMode === 'stock-controller' && Boolean(stockControllerAccess);
  const isDirectCompanyAccess = pathMode === 'company' && Boolean(directCompanyAccessKey);
  const [view, setView] = useState(() => (pathMode === 'control' ? 'architect' : 'menu'));
  const [animationClass, setAnimationClass] = useState('');
  const [isAnimating, setIsAnimating] = useState(false);
  const [currentUser, setCurrentUser] = useState(null);
  const [currentUserData, setCurrentUserData] = useState(null);
  const [isUserDataLoading, setIsUserDataLoading] = useState(true);
  const [isAuthReady, setIsAuthReady] = useState(false);
  const [showSplash, setShowSplash] = useState(true);
  const [message, setMessage] = useState({ text: '', type: 'info' });
  const [soundEnabled, setSoundEnabled] = useState(() => JSON.parse(localStorage.getItem('soundEnabled')) ?? true);
  const [showCompanySettingsModal, setShowCompanySettingsModal] = useState(false);
  const [showTechnicalSettingsModal, setShowTechnicalSettingsModal] = useState(false);
  const [isAuthenticatedWithPassword, setIsAuthenticatedWithPassword] = useState(false);
  const [selectedTransport, setSelectedTransport] = useState(null);
  const [logisticsRouteIntent, setLogisticsRouteIntent] = useState(null);
  const [logisticsRoutes, setLogisticsRoutes] = useState([]);
  const [companyAccessState, setCompanyAccessState] = useState(() => (
    isDirectCompanyAccess
      ? {
          status: 'loading',
          requestedKey: directCompanyAccessKey,
          resolvedCompanyId: null,
          resolvedCompanyName: '',
          message: '',
        }
      : {
          status: 'idle',
          requestedKey: '',
          resolvedCompanyId: null,
          resolvedCompanyName: '',
          message: '',
      }
  ));
  const [architectCompanyOptions, setArchitectCompanyOptions] = useState([]);
  const [companyId, setCompanyId] = useState(() => (
    isDirectCompanyAccess || isStockControllerAccess
      ? null
      : localStorage.getItem('activeCompanyId') || DEFAULT_COMPANY_ID
  ));
  const [companyName, setCompanyName] = useState(DEFAULT_COMPANY_NAME);
  const [companyGeneralSettings, setCompanyGeneralSettings] = useState(null);
  const [companyModules, setCompanyModules] = useState(null);
  const [companyPlanSettings, setCompanyPlanSettings] = useState(null);
  const [publicPlanInterest, setPublicPlanInterest] = useState(() => {
    if (typeof window === 'undefined') return 'base';
    return window.localStorage.getItem('gcentra_public_plan_interest') || 'base';
  });

  const tutorial = useTutorial({
    view,
    setView,
    selectedTransport,
    scopeKey: companyId || DEFAULT_COMPANY_ID,
  });
  const {
    currentTutorialId,
    tutorialRun,
    currentTutorialTarget,
    queuedTutorialId,
    tutorialSeen,
    logisticsTutorialSeen,
    routeCreatorTutorialSeen,
    serviceTutorialSeen,
    monitorTutorialSeen,
    reportsTutorialSeen,
    fleetExpensesTutorialSeen,
    stockTutorialSeen,
    unitModalTutorialSeen,
    unitModalTutorialRepeatEnabled,
    startDashboardTutorial,
    startLogisticsTutorial,
    startRouteCreatorTutorial,
    startServiceTutorial,
    startMonitorTutorial,
    startReportsTutorial,
    startFleetExpensesTutorial,
    startStockTutorial,
    startUnitModalTutorial,
    toggleUnitModalTutorialRepeat,
    setMonitorTutorialHasActiveRoutes,
    overlayProps: tutorialOverlayProps,
  } = tutorial;
  const [allUsers, setAllUsers] = useState([]);
  const [companyAccessRetryNonce, setCompanyAccessRetryNonce] = useState(0);
  const [dashboardPlansOpen, setDashboardPlansOpen] = useState(false);
  const [giaPanelOpen, setGiaPanelOpen] = useState(false);
  const [giaRuntimeContext, setGiaRuntimeContext] = useState({ subsectionLabel: '', focusLabel: '' });

  const moduleKeysByView = useMemo(() => ({
    stock: 'stock',
    logistics: 'logistics',
    service: 'service',
    monitor: 'routeMonitor',
    reports: 'reports',
    fleetExpenses: 'fleetExpenses',
    systemGuide: 'systemGuide',
    database: 'logistics',
  }), []);

  const moduleLabelsByView = useMemo(() => ({
    stock: 'Stock y movimientos',
    logistics: 'Logística',
    service: 'Servicio',
    monitor: 'Monitor en tiempo real',
    reports: 'Reportes y liquidación',
    fleetExpenses: 'Gastos de Flota',
    systemGuide: 'Guía y soporte',
    database: 'Base de datos',
  }), []);


const synths = useRef(null);
const hasHydratedInitialCompanyRef = useRef(false);
const permissionWarnedRef = useRef(new Set());
const messageTimerRef = useRef(null);
const lastCommercialStatusRef = useRef('');
const isArchitectView = view === 'architect';

const handleGiaContextChange = useCallback((nextContext = {}) => {
  setGiaRuntimeContext({
    subsectionLabel: String(nextContext.subsectionLabel || '').trim(),
    focusLabel: String(nextContext.focusLabel || '').trim(),
  });
}, []);

const giaSubsection = useMemo(() => {
  const fallback = resolveGiaSubsection({
    view,
    isArchitectView,
    selectedTransport,
    dashboardPlansOpen,
  });

  return {
    subsectionLabel: giaRuntimeContext.subsectionLabel || fallback.subsectionLabel,
    focusLabel: giaRuntimeContext.focusLabel || fallback.focusLabel,
  };
}, [dashboardPlansOpen, giaRuntimeContext.focusLabel, giaRuntimeContext.subsectionLabel, isArchitectView, selectedTransport, view]);

useEffect(() => {
  if (typeof window === 'undefined') return undefined;

  const handlePopState = () => setCurrentPathname(window.location.pathname || '/');

  window.addEventListener('popstate', handlePopState);
  return () => window.removeEventListener('popstate', handlePopState);
}, []);

useEffect(() => {
  if (typeof window === 'undefined') return;

  trackPublicPageVisit({
    pathname: currentPathname,
    pathMode,
  }).catch((trafficError) => {
    console.error('No se pudo registrar la visita pública:', trafficError);
  });
}, [currentPathname, pathMode]);

const navigateToPath = useCallback((nextPath) => {
  if (typeof window === 'undefined') return;

  const targetPath = String(nextPath || '/').trim() || '/';
  if (window.location.pathname !== targetPath) {
    window.history.pushState({}, '', targetPath);
  }
  setCurrentPathname(targetPath);
}, []);

useEffect(() => {
  setGiaRuntimeContext({ subsectionLabel: '', focusLabel: '' });
}, [companyId, selectedTransport?.id, view]);

const canAccessArchitectPanel = useMemo(() => {
  if (!currentUser) return false;
  return canAccessControlPanel(currentUserData, currentUser.email);
}, [
  currentUser,
  currentUserData,
]);

  const primaryCompanyAccessPath = useMemo(() => {
    if (isDirectCompanyAccess && directCompanyAccessKey) {
      return `/empresa/${encodeURIComponent(directCompanyAccessKey)}`;
    }

    const candidate = currentUserData?.accessSlug || currentUserData?.companySlug || currentUserData?.companyId;
    if (candidate) {
      return `/empresa/${encodeURIComponent(candidate)}`;
    }

    const fallbackOptions = [
      ...(Array.isArray(currentUserData?.allowedCompanies) ? currentUserData.allowedCompanies : []),
      ...(Array.isArray(currentUserData?.availableCompanies) ? currentUserData.availableCompanies : []),
      ...(Array.isArray(currentUserData?.companies) ? currentUserData.companies : []),
    ].map((option) => normalizeCompanyOption(option)).filter(Boolean);

    const fallback = fallbackOptions[0];
    return fallback?.id ? `/empresa/${encodeURIComponent(fallback.id)}` : '';
  }, [
    currentUserData?.accessSlug,
    currentUserData?.allowedCompanies,
    currentUserData?.availableCompanies,
    currentUserData?.companies,
    currentUserData?.companyId,
    currentUserData?.companySlug,
    directCompanyAccessKey,
    isDirectCompanyAccess,
  ]);

const companyOptions = useMemo(() => {
  if (canAccessArchitectPanel) {
    const collected = new Map();

    architectCompanyOptions.forEach((option) => {
      collected.set(option.id, option);
    });

    if (companyId && !collected.has(companyId)) {
      collected.set(companyId, { id: companyId, name: companyName || companyId });
    }

    return Array.from(collected.values());
  }
  const collected = new Map();

  const directOption = normalizeCompanyOption(
    currentUserData?.companyId && currentUserData.companyId !== DEFAULT_COMPANY_ID
      ? {
          id: currentUserData.companyId,
          name: currentUserData.companyName || currentUserData.companyId,
          accessSlug: currentUserData.accessSlug || currentUserData.companySlug || '',
        }
      : null
  );

  if (directOption) collected.set(directOption.id, directOption);

  const rawOptions = [
    ...(Array.isArray(currentUserData?.companies) ? currentUserData.companies : []),
    ...(Array.isArray(currentUserData?.availableCompanies) ? currentUserData.availableCompanies : []),
    ...(Array.isArray(currentUserData?.allowedCompanies) ? currentUserData.allowedCompanies : []),
  ];

  rawOptions
    .map(normalizeCompanyOption)
    .filter(Boolean)
    .forEach((option) => {
      collected.set(option.id, option);
    });

  if (companyId && !collected.has(companyId)) {
    collected.set(companyId, { id: companyId, name: companyName || companyId });
  }

  return Array.from(collected.values());
}, [
  architectCompanyOptions,
  canAccessArchitectPanel,
  companyId,
  companyName,
  currentUserData,
  ]);

  const giaContextPacket = useMemo(
    () =>
      buildGiaContextPacket({
        view,
        companyName,
        selectedTransport,
        isArchitectView,
        subsectionLabel: giaSubsection.subsectionLabel,
        focusLabel: giaSubsection.focusLabel,
      }),
    [companyName, giaSubsection.focusLabel, giaSubsection.subsectionLabel, isArchitectView, selectedTransport, view]
  );
  const giaSectionLabel = giaContextPacket.sectionLabel;
  const giaQuickAnswers = useMemo(
    () =>
      buildGiaQuickAnswers(view, {
        companyName,
        selectedTransport,
        isArchitectView,
        subsectionLabel: giaSubsection.subsectionLabel,
        focusLabel: giaSubsection.focusLabel,
      }),
    [companyName, giaSubsection.focusLabel, giaSubsection.subsectionLabel, isArchitectView, selectedTransport, view]
  );
  const giaFlowKnowledge = useMemo(() => {
    const flowKey = GIA_FLOW_BY_VIEW[isArchitectView ? 'architect' : view];
    return flowKey ? getGiaFlowKnowledge(flowKey) : null;
  }, [isArchitectView, view]);
  const resolvedCompanyModules = useMemo(
    () =>
      resolveModulesSettingsWithPlanFallback({
        generalSettings: companyGeneralSettings || {},
        planSettings: companyPlanSettings || {},
        modulesSettings: companyModules || {},
      }),
    [companyGeneralSettings, companyModules, companyPlanSettings]
  );
  const enabledModulesCount = useMemo(() => getEnabledModulesCount(resolvedCompanyModules), [resolvedCompanyModules]);
  const companyCommercialState = useMemo(() => resolveCommercialState({
    generalSettings: companyGeneralSettings || {},
    planSettings: companyPlanSettings || {},
    modulesSettings: resolvedCompanyModules,
  }), [companyGeneralSettings, companyPlanSettings, resolvedCompanyModules]);
  const companyPlanHeaderTone = useMemo(() => {
    if (companyCommercialState?.tone === 'red') return 'rose';
    if (companyCommercialState?.tone === 'amber') return 'violet';
    if (companyCommercialState?.tone === 'cyan') return 'cyan';
    return 'emerald';
  }, [companyCommercialState?.tone]);
  const companyPlanHeaderLabel = useMemo(() => {
    const planLabel = companyCommercialState?.planMeta?.label || 'Plan actual';

    if (companyCommercialState?.status === 'trial_active') {
      return companyCommercialState?.countdownLabel ? `${planLabel} · ${companyCommercialState.countdownLabel}` : planLabel;
    }

    if (companyCommercialState?.status === 'trial_expiring') {
      return companyCommercialState?.countdownLabel ? `${planLabel} · ${companyCommercialState.countdownLabel}` : `${planLabel} · Por vencer`;
    }

    if (companyCommercialState?.status === 'trial_expired') {
      return `${planLabel} · Vencido`;
    }

    if (companyCommercialState?.status === 'subscription_expiring') {
      return companyCommercialState?.countdownLabel ? `${planLabel} · ${companyCommercialState.countdownLabel}` : `${planLabel} · Renovación próxima`;
    }

    if (companyCommercialState?.status === 'subscription_expired') {
      return `${planLabel} · Vencido`;
    }

    if (companyCommercialState?.nextDateLabel) {
      return `${planLabel} · ${companyCommercialState.nextDateLabel}`;
    }

    return planLabel;
  }, [
    companyCommercialState?.countdownLabel,
    companyCommercialState?.nextDateLabel,
    companyCommercialState?.planMeta?.label,
    companyCommercialState?.status,
  ]);
  const enabledQuickNavViews = useMemo(() => { 
    if (companyCommercialState?.shouldRestrictOperations) return ['menu'];
    return ['menu', ...NAV_MODULES
      .filter(({ moduleKey }) => resolvedCompanyModules?.[moduleKey]?.enabled !== false)
      .map(({ viewKey }) => viewKey)];
  }, [companyCommercialState?.shouldRestrictOperations, resolvedCompanyModules]);
useEffect(() => {
  setDashboardPlansOpen(false);
}, [companyId]);
useEffect(() => {
  setGiaPanelOpen(false);
}, [companyId, isArchitectView, selectedTransport?.id, view]);
const handleHeaderPlansClick = useCallback(() => {
  if (view !== 'menu') {
    setSelectedTransport(null);
    setLogisticsRouteIntent(null);
    setView('menu');
    setDashboardPlansOpen(true);
    return;
  }

  setDashboardPlansOpen((prev) => !prev);
}, [view]);

  useEffect(() => {
    if (pathMode === 'control' && canAccessArchitectPanel && view !== 'architect') {
      setView('architect');
      return;
    }

    if (pathMode !== 'control' && view === 'architect') {
      setView('menu');
    }
  }, [canAccessArchitectPanel, pathMode, view]);

  const shouldResolveDirectCompanyAccess = isDirectCompanyAccess && Boolean(directCompanyAccessKey);
  const isDirectCompanyAccessResolved = companyAccessState.status === 'resolved';
  const isDirectCompanyAccessBlocked = ['not-found', 'inactive', 'error'].includes(companyAccessState.status);
  const isDirectCompanyAccessPending = shouldResolveDirectCompanyAccess && ['idle', 'loading'].includes(companyAccessState.status);

  const showMessage = useCallback((text, type = 'info', duration = 4000) => {
    if (messageTimerRef.current) clearTimeout(messageTimerRef.current);
    setMessage({ text, type });
    messageTimerRef.current = setTimeout(() => setMessage({ text: '', type: 'info' }), duration);
  }, []);

  const reportFirestoreError = useCallback((scope, error) => {
    console.error(`ERROR ${scope}:`, error);

    if (isPermissionDeniedError(error)) {
      if (!permissionWarnedRef.current.has(scope)) {
        permissionWarnedRef.current.add(scope);
        showMessage(`Sin permisos en Firestore para ${scope}.`, 'error', 6000);
      }
      return;
    }

    showMessage(`Error en ${scope}.`, 'error', 5000);
  }, [showMessage]);

  useEffect(() => {
    if (!canAccessArchitectPanel) {
      setArchitectCompanyOptions([]);
      return undefined;
    }

    const companiesRef = collection(db, resolveCompaniesCollectionPath());

    const unsubscribe = onSnapshot(
      companiesRef,
      (snapshot) => {
        const next = snapshot.docs
          .map((docSnap) => {
            const data = docSnap.data() || {};
            return normalizeCompanyOption({
              id: data.companyId || docSnap.id,
              name: data.companyName || data.name || docSnap.id,
              accessSlug: data.accessSlug || data.companySlug || '',
              accessPath: data.accessPath || '',
            });
          })
          .filter(Boolean);

        setArchitectCompanyOptions(next);
      },
      (error) => {
        console.error('Error leyendo empresas para Arquitectura:', error);
        setArchitectCompanyOptions([]);
        reportFirestoreError('architectCompanyOptions', error);
      }
    );

    return () => unsubscribe();
  }, [canAccessArchitectPanel, reportFirestoreError]);

  useEffect(() => {
    const style = document.createElement('style');
    style.innerHTML = `
      @keyframes glow { 0%, 100% { filter: drop-shadow(0 0 4px rgba(147, 197, 253, 0.6)); } 50% { filter: drop-shadow(0 0 12px rgba(147, 197, 253, 1)); } }
      .animate-glow { animation: glow 4s ease-in-out infinite; }
      .text-3d-effect { text-shadow: 0px 1px 0px rgba(0,0,0,0.4), 0px 2px 2px rgba(0,0,0,0.3), 0px 3px 3px rgba(0,0,0,0.2); }
      @keyframes sweep { 0% { transform: translateX(-100%) skewX(-20deg); } 100% { transform: translateX(250%) skewX(-20deg); } }
      .header-sweep::after { content: ''; position: absolute; top: 0; left: 0; width: 40%; height: 100%; background: linear-gradient(90deg, rgba(255, 255, 255, 0) 0%, rgba(255, 255, 255, 0.15) 50%, rgba(255, 255, 255, 0) 100%); animation: sweep 5s infinite linear; animation-delay: 2s; }
      .header-sweep::before { content: ''; position: absolute; top: 0; right: 0; width: 30%; height: 100%; background: linear-gradient(-90deg, rgba(255, 255, 255, 0) 0%, rgba(255, 255, 255, 0.1) 50%, rgba(255, 255, 255, 0) 100%); animation: sweep 7s infinite linear; animation-delay: 4s; }
      @keyframes slide-out-up { from { transform: translateY(0); opacity: 1; } to { transform: translateY(-50px); opacity: 0; } }
      .animate-slide-out-up { animation: slide-out-up 0.3s ease-out forwards; }
      @keyframes slide-in-down { from { transform: translateY(-50px); opacity: 0; } to { transform: translateY(0); opacity: 1; } }
      .animate-slide-in-down { animation: slide-in-down 0.3s ease-out forwards; }
    `;
    document.head.appendChild(style);

    return () => {
      document.head.removeChild(style);
      if (messageTimerRef.current) clearTimeout(messageTimerRef.current);
    };
  }, []);

  useEffect(() => {
    localStorage.setItem('soundEnabled', JSON.stringify(soundEnabled));
  }, [soundEnabled]);

  useEffect(() => {
    const timer = setTimeout(() => setShowSplash(false), 2200);
    return () => clearTimeout(timer);
  }, []);

  useEffect(() => {
    if (firebaseInitError) {
      setIsAuthReady(true);
      return undefined;
    }

    const unsubscribe = onAuthStateChanged(auth, (user) => {
      setCurrentUser(user);
      setIsAuthReady(true);
    });

    return () => unsubscribe();
  }, []);

  useEffect(() => {
    if (currentUser?.uid) {
      const alreadyVerified = localStorage.getItem(`admin_pass_verified_${currentUser.uid}`) === 'true';
      setIsAuthenticatedWithPassword(alreadyVerified);
    } else {
      setIsAuthenticatedWithPassword(false);
      hasHydratedInitialCompanyRef.current = false;
    }
  }, [currentUser]);

  useEffect(() => {
    if (isStockControllerAccess) return;
    if (shouldResolveDirectCompanyAccess) return;
    if (isUserDataLoading || hasHydratedInitialCompanyRef.current) return;

    const storedCompanyId = localStorage.getItem('activeCompanyId');
    const preferredCompanyId = storedCompanyId || currentUserData?.companyId || DEFAULT_COMPANY_ID;

    hasHydratedInitialCompanyRef.current = true;

    if (preferredCompanyId && preferredCompanyId !== companyId) {
      setCompanyId(preferredCompanyId);
    }
  }, [companyId, currentUserData?.companyId, isStockControllerAccess, isUserDataLoading, shouldResolveDirectCompanyAccess]);

  useEffect(() => {
    if (!shouldResolveDirectCompanyAccess || !db) return undefined;

    let cancelled = false;

    const resolveAccess = async () => {
      const requestedKey = String(directCompanyAccessKey || '').trim();
      if (!requestedKey) {
        if (!cancelled) {
          setCompanyAccessState({
            status: 'not-found',
            requestedKey: '',
            resolvedCompanyId: null,
            resolvedCompanyName: '',
            message: 'El acceso no incluye una empresa válida.',
          });
        }
        return;
      }

      setCompanyAccessState((prev) => ({
        ...prev,
        status: 'loading',
        requestedKey,
        message: '',
      }));

      try {
        const directRef = doc(db, resolveCompaniesCollectionPath(), requestedKey);
        const directSnap = await getDoc(directRef);

        let matchedCompany = directSnap.exists()
          ? { id: directSnap.id, data: directSnap.data() || {} }
          : null;

        if (!matchedCompany) {
          const accessSlugSnap = await getDocs(
            query(collection(db, resolveCompaniesCollectionPath()), where('accessSlug', '==', requestedKey))
          );

          const accessSlugMatch = accessSlugSnap.docs[0];
          if (accessSlugMatch) {
            matchedCompany = {
              id: accessSlugMatch.id,
              data: accessSlugMatch.data() || {},
            };
          }
        }

        if (!matchedCompany) {
          const legacySlugSnap = await getDocs(
            query(collection(db, resolveCompaniesCollectionPath()), where('companySlug', '==', requestedKey))
          );

          const legacySlugMatch = legacySlugSnap.docs[0];
          if (legacySlugMatch) {
            matchedCompany = {
              id: legacySlugMatch.id,
              data: legacySlugMatch.data() || {},
            };
          }
        }

        if (!matchedCompany) {
          const normalizedRequestedKey = sanitizeCompanyAccessSlug(requestedKey, requestedKey);
          const allCompaniesSnap = await getDocs(collection(db, resolveCompaniesCollectionPath()));
          const prefixMatch = allCompaniesSnap.docs.find((docSnap) => {
            const data = docSnap.data() || {};
            const rawCompanyId = String(data.companyId || docSnap.id || '').trim().toLowerCase();
            const storedAccessSlug = sanitizeCompanyAccessSlug(
              data.accessSlug || data.companySlug || data.companyName || '',
              rawCompanyId
            );

            return (
              storedAccessSlug === normalizedRequestedKey ||
              rawCompanyId === normalizedRequestedKey ||
              rawCompanyId.startsWith(`${normalizedRequestedKey}-`)
            );
          });

          if (prefixMatch) {
            matchedCompany = {
              id: prefixMatch.id,
              data: prefixMatch.data() || {},
            };
          }
        }

        if (!matchedCompany) {
          if (!cancelled) {
            setCompanyAccessState({
              status: 'not-found',
              requestedKey,
              resolvedCompanyId: null,
              resolvedCompanyName: '',
              message: 'No encontramos una empresa asociada a este acceso.',
            });
            setCompanyId(null);
            setCompanyName(DEFAULT_COMPANY_NAME);
          }
          return;
        }

        let generalSettings = {};
        try {
          const generalSnap = await getDoc(doc(db, resolveCompanyGeneralSettingsPath(matchedCompany.id)));
          generalSettings = generalSnap.data() || {};
        } catch (error) {
          if (!isPermissionDeniedError(error) || currentUser) {
            throw error;
          }
        }

        const resolvedCompanyName = generalSettings.companyName || matchedCompany.data.companyName || matchedCompany.id;
        const accessState = resolveCompanyAccessState(matchedCompany.data, generalSettings);

        if (!cancelled && accessState === 'inactive') {
          setCompanyAccessState({
            status: 'inactive',
            requestedKey,
            resolvedCompanyId: matchedCompany.id,
            resolvedCompanyName,
            message: 'La empresa existe, pero su acceso figura como inactivo.',
          });
          setCompanyId(null);
          setCompanyName(resolvedCompanyName);
          return;
        }

        if (!cancelled) {
          setCompanyAccessState({
            status: 'resolved',
            requestedKey,
            resolvedCompanyId: matchedCompany.id,
            resolvedCompanyName,
            message: '',
          });
          setCompanyId(matchedCompany.id);
          setCompanyName(resolvedCompanyName);
          hasHydratedInitialCompanyRef.current = true;
        }
      } catch (error) {
        if (cancelled) return;

        if (isPermissionDeniedError(error) && !currentUser) {
          setCompanyAccessState({
            status: 'auth-required',
            requestedKey: directCompanyAccessKey,
            resolvedCompanyId: null,
            resolvedCompanyName: '',
            message: '',
          });
          return;
        }

        console.error('Error resolviendo acceso de empresa:', error);

        setCompanyAccessState({
          status: 'error',
          requestedKey: directCompanyAccessKey,
          resolvedCompanyId: null,
          resolvedCompanyName: '',
          message: isPermissionDeniedError(error)
            ? 'No se pudo validar este acceso con los permisos actuales.'
            : 'No pudimos validar este acceso en este momento.',
        });
      }
    };

    resolveAccess();

    return () => {
      cancelled = true;
    };
  }, [companyAccessRetryNonce, currentUser, db, directCompanyAccessKey, shouldResolveDirectCompanyAccess]);

  useEffect(() => {
    if (!shouldResolveDirectCompanyAccess || companyAccessState.status !== 'resolved') return;
    if (companyId !== companyAccessState.resolvedCompanyId) {
      setCompanyId(companyAccessState.resolvedCompanyId);
    }
    if (companyAccessState.resolvedCompanyName && companyName !== companyAccessState.resolvedCompanyName) {
      setCompanyName(companyAccessState.resolvedCompanyName);
    }
    if (view === 'architect') {
      setView('menu');
    }
  }, [companyAccessState, companyId, companyName, shouldResolveDirectCompanyAccess, view]);

  useEffect(() => {
    if (!companyId || isStockControllerAccess) return;
    localStorage.setItem('activeCompanyId', companyId);
    setSelectedTransport(null);
  }, [companyId, isStockControllerAccess]);

  useEffect(() => {
    if (isStockControllerAccess) return undefined;
    if (!db || !currentUser?.uid || !companyId || firebaseInitError) return undefined;

    ensureCompanyBootstrap(companyId).catch((error) => {
      console.error('Error inicializando empresa base:', error);
      reportFirestoreError(`companyBootstrap/${companyId}`, error);
    });

    return undefined;
  }, [companyId, currentUser?.uid, isStockControllerAccess, reportFirestoreError]);

  useEffect(() => {
    if (isStockControllerAccess) return undefined;
    if (!db || !companyId) return undefined;

    const generalRef = doc(db, resolveCompanyGeneralSettingsPath(companyId));
    const modulesRef = doc(db, resolveCompanyModulesSettingsPath(companyId));
    const planRef = doc(db, resolveCompanyPlanSettingsPath(companyId));

    const unsubscribeGeneral = onSnapshot(
      generalRef,
      (snapshot) => {
        const data = snapshot.data() || {};
        setCompanyGeneralSettings(data);
        setCompanyName(data.companyName || (companyId === DEFAULT_COMPANY_ID ? DEFAULT_COMPANY_NAME : companyId));
      },
      () => {
        setCompanyGeneralSettings(null);
        setCompanyName(companyId === DEFAULT_COMPANY_ID ? DEFAULT_COMPANY_NAME : companyId);
      }
    );

    const unsubscribeModules = onSnapshot(
      modulesRef,
      (snapshot) => setCompanyModules(snapshot.data() || null),
      () => setCompanyModules(null)
    );

    const unsubscribePlan = onSnapshot(
      planRef,
      (snapshot) => setCompanyPlanSettings(snapshot.data() || null),
      () => setCompanyPlanSettings(null)
    );

    return () => {
      unsubscribeGeneral();
      unsubscribeModules();
      unsubscribePlan();
    };
  }, [companyId, isStockControllerAccess]);

  const playSound = async (type) => {
    if (!soundEnabled) return;

    if (!synths.current) {
      await Tone.start();
      synths.current = {
        start: new Tone.PolySynth(Tone.Synth, {
          oscillator: { type: 'fatsawtooth', count: 3, spread: 30 },
          envelope: { attack: 0.01, decay: 0.1, sustain: 0.5, release: 0.4, attackCurve: 'exponential' },
        }).toDestination(),
        add: new Tone.Synth({ oscillator: { type: 'triangle' }, envelope: { attack: 0.01, decay: 0.2, sustain: 0.1, release: 0.1 } }).toDestination(),
        drop: new Tone.Synth({ oscillator: { type: 'sine' }, envelope: { attack: 0.01, decay: 0.1, sustain: 0.2, release: 0.2 } }).toDestination(),
        complete: new Tone.PolySynth(Tone.Synth, {
          oscillator: { type: 'fatsawtooth' },
          envelope: { attack: 0.01, decay: 0.4, sustain: 0.2, release: 0.4 },
        }).toDestination(),
      };
      if (type === 'start') synths.current.start.triggerAttackRelease(['C4', 'E4', 'G4', 'B4'], '2n');
    }

    if (Tone.context.state !== 'running') await Tone.start();

    switch (type) {
      case 'add':
        synths.current.add.triggerAttackRelease('E5', '8n');
        break;
      case 'drop':
        synths.current.drop.triggerAttackRelease('G5', '8n');
        break;
      case 'complete':
        synths.current.complete.triggerAttackRelease(['C5', 'E5', 'G5'], '8n');
        break;
      default:
        break;
    }
  };

  useEffect(() => {
    if (!db || !appId || !companyId || view !== 'logistics') {
      setLogisticsRoutes([]);
      return undefined;
    }

    let companyRoutes = [];
    let legacyRoutes = [];
    const syncRoutes = () => {
      setLogisticsRoutes(mergeLogisticsRoutes(companyRoutes, legacyRoutes));
    };

    const unsubscribeCompany = onSnapshot(
      collection(db, resolveRoutesCollectionPath(companyId)),
      (snapshot) => {
        companyRoutes = snapshot.docs.map((docItem) => ({ id: docItem.id, ...docItem.data() }));
        syncRoutes();
      },
      (error) => {
        companyRoutes = [];
        reportFirestoreError(`companyRoutes/${companyId}`, error);
        syncRoutes();
      }
    );

    const unsubscribeLegacy = onSnapshot(
      collection(db, `artifacts/${appId}/logisticsRoutes`),
      (snapshot) => {
        legacyRoutes = snapshot.docs.map((docItem) => ({ id: docItem.id, ...docItem.data(), _source: 'legacy' }));
        syncRoutes();
      },
      (error) => {
        legacyRoutes = [];
        reportFirestoreError('legacyLogisticsRoutes', error);
        syncRoutes();
      }
    );

    return () => {
      unsubscribeCompany();
      unsubscribeLegacy();
    };
  }, [appId, companyId, reportFirestoreError, view]);

  useEffect(() => {
    if (!currentUser) {
      setCurrentUserData(null);
      setIsUserDataLoading(false);
      return undefined;
    }

    if (shouldResolveDirectCompanyAccess && !isDirectCompanyAccessResolved) {
      setIsUserDataLoading(true);
      return undefined;
    }

    setIsUserDataLoading(true);

    const resolvedFallbackCompanyId =
      companyAccessState.resolvedCompanyId || companyId || DEFAULT_COMPANY_ID;
    const resolvedFallbackCompanyName =
      companyAccessState.resolvedCompanyName ||
      companyName ||
      (resolvedFallbackCompanyId === DEFAULT_COMPANY_ID ? DEFAULT_COMPANY_NAME : resolvedFallbackCompanyId);
    const hasConcreteCompanyContext =
      Boolean(resolvedFallbackCompanyId) && resolvedFallbackCompanyId !== DEFAULT_COMPANY_ID;

    const fallbackUserData = isControlAccessEmail(currentUser.email)
      ? buildControlUserProfile(currentUser)
      : hasConcreteCompanyContext
        ? buildCompanyScopedFallbackUser({
            currentUser,
            companyId: resolvedFallbackCompanyId,
            companyName: resolvedFallbackCompanyName,
            accessSlug: directCompanyAccessKey,
          })
        : {
            uid: currentUser.uid,
            email: String(currentUser.email || '').trim().toLowerCase(),
            role: 'transportista',
            companyId: DEFAULT_COMPANY_ID,
          };

    let resolvedUserData = false;
    const loadingGuard = setTimeout(() => {
      if (resolvedUserData) return;
      setCurrentUserData((prev) => prev || fallbackUserData);
      setIsUserDataLoading(false);
    }, 2500);

    const userDocUnsub = onSnapshot(
      doc(db, 'users', currentUser.uid),
      async (userDocSnap) => {
        if (userDocSnap.exists()) {
          const nextUserData = { uid: userDocSnap.id, ...userDocSnap.data() };
          const shouldRepairInternalAccess =
            isControlAccessEmail(currentUser.email) &&
            !canAccessControlPanel(nextUserData, currentUser.email);
          const primaryCompanyOption = derivePrimaryCompanyOptionFromUserData(nextUserData);
          const isBrokenCompanyScopedUser =
            (nextUserData.accessScope === 'company' || nextUserData.isTrialOwner === true) &&
            primaryCompanyOption &&
            (
              String(nextUserData.role || '').trim().toLowerCase() === 'transportista' ||
              !nextUserData.companyId ||
              nextUserData.companyId === DEFAULT_COMPANY_ID
            );

          if (shouldRepairInternalAccess) {
            const repairedControlUser = buildControlUserProfile(currentUser);
            try {
              await setDoc(
                doc(db, 'users', currentUser.uid),
                {
                  ...repairedControlUser,
                  updatedAt: serverTimestamp(),
                },
                { merge: true }
              );
              resolvedUserData = true;
              setCurrentUserData(repairedControlUser);
              setIsUserDataLoading(false);
              return;
            } catch (error) {
              reportFirestoreError('users/repairInternalAccess', error);
            }
          }

          if (isBrokenCompanyScopedUser) {
            const repairedCompanyUser = {
              ...nextUserData,
              role: 'companyAdmin',
              accessScope: 'company',
              companyId: primaryCompanyOption.id,
              companyIds: [primaryCompanyOption.id],
              companyName:
                nextUserData.companyName ||
                primaryCompanyOption.name ||
                nextUserData.companyId,
              accessSlug:
                String(nextUserData.accessSlug || '').trim() ||
                String(nextUserData.companySlug || '').trim() ||
                String(primaryCompanyOption.accessSlug || '').trim() ||
                sanitizeCompanyAccessSlug(primaryCompanyOption.name || primaryCompanyOption.id, primaryCompanyOption.id),
              companySlug:
                String(nextUserData.companySlug || '').trim() ||
                String(nextUserData.accessSlug || '').trim() ||
                String(primaryCompanyOption.accessSlug || '').trim() ||
                sanitizeCompanyAccessSlug(primaryCompanyOption.name || primaryCompanyOption.id, primaryCompanyOption.id),
              updatedAt: serverTimestamp(),
            };

            try {
              await setDoc(doc(db, 'users', currentUser.uid), repairedCompanyUser, { merge: true });
              const repairedSnapshotUser = {
                ...nextUserData,
                ...repairedCompanyUser,
                updatedAt: new Date().toISOString(),
              };

              try {
                const companyRef = doc(db, resolveCompanyDocPath(primaryCompanyOption.id));
                const companySnap = await getDoc(companyRef);

                if (!companySnap.exists()) {
                  const repairedCompanyName =
                    repairedSnapshotUser.companyName ||
                    primaryCompanyOption.name ||
                    primaryCompanyOption.id;
                  const repairedAccessSlug =
                    String(repairedSnapshotUser.accessSlug || '').trim() ||
                    String(repairedSnapshotUser.companySlug || '').trim() ||
                    String(primaryCompanyOption.accessSlug || '').trim() ||
                    sanitizeCompanyAccessSlug(repairedCompanyName, primaryCompanyOption.id);
                  const isTrialRecovery = repairedSnapshotUser.isTrialOwner === true;
                  const recoveredPlanName = isTrialRecovery ? 'Plan Trial' : 'Plan Base';
                  const recoveredPlanId = isTrialRecovery ? 'trial' : 'base';
                  const recoveredStatus = isTrialRecovery ? 'trial' : 'active';

                  await ensureCompanyBootstrap({
                    companyId: primaryCompanyOption.id,
                    companyName: repairedCompanyName,
                    status: recoveredStatus,
                    planId: recoveredPlanId,
                    planName: recoveredPlanName,
                    active: true,
                    isSandbox: false,
                    main: {
                      accessSlug: repairedAccessSlug,
                      companySlug: repairedAccessSlug,
                      trialStatus: isTrialRecovery ? 'active' : null,
                    },
                  });

                  const recoveryPayload = {
                    companyId: primaryCompanyOption.id,
                    companyName: repairedCompanyName,
                    accessSlug: repairedAccessSlug,
                    companySlug: repairedAccessSlug,
                    responsibleName: '',
                    contactEmail: repairedSnapshotUser.email || '',
                    contactPhone: '',
                    officeName: repairedCompanyName,
                    active: true,
                    isActive: true,
                    status: recoveredStatus,
                    plan: recoveredPlanName,
                    planId: recoveredPlanId,
                    planName: recoveredPlanName,
                    trialStatus: isTrialRecovery ? 'active' : null,
                    updatedAt: serverTimestamp(),
                  };

                  await Promise.all([
                    setDoc(companyRef, recoveryPayload, { merge: true }),
                    setDoc(
                      doc(db, resolveCompanyMainSettingsPath(primaryCompanyOption.id)),
                      {
                        companyId: primaryCompanyOption.id,
                        companyName: repairedCompanyName,
                        accessSlug: repairedAccessSlug,
                        companySlug: repairedAccessSlug,
                        trialStatus: isTrialRecovery ? 'active' : null,
                        updatedAt: serverTimestamp(),
                      },
                      { merge: true }
                    ),
                    setDoc(
                      doc(db, resolveCompanyGeneralSettingsPath(primaryCompanyOption.id)),
                      recoveryPayload,
                      { merge: true }
                    ),
                    setDoc(
                      doc(db, resolveCompanyPlanSettingsPath(primaryCompanyOption.id)),
                      {
                        planId: recoveredPlanId,
                        planName: recoveredPlanName,
                        label: recoveredPlanName,
                        status: recoveredStatus,
                        trialStatus: isTrialRecovery ? 'active' : null,
                        updatedAt: serverTimestamp(),
                      },
                      { merge: true }
                    ),
                    setDoc(
                      doc(db, resolveCompanyModulesSettingsPath(primaryCompanyOption.id)),
                      {
                        logistics: { enabled: true },
                        systemGuide: { enabled: true },
                        updatedAt: serverTimestamp(),
                      },
                      { merge: true }
                    ),
                  ]);
                }
              } catch (error) {
                reportFirestoreError('companies/repairBootstrap', error);
              }

              resolvedUserData = true;
              setCurrentUserData(repairedSnapshotUser);
              setIsUserDataLoading(false);
              return;
            } catch (error) {
              reportFirestoreError('users/repairCompanyAccess', error);
            }
          }

          resolvedUserData = true;
          setCurrentUserData(nextUserData);
          setIsUserDataLoading(false);
          return;
        }

        setCurrentUserData((prev) => prev || fallbackUserData);
        setIsUserDataLoading(false);

        const shouldPersistFallbackUser =
          isControlAccessEmail(currentUser.email) ||
          (hasConcreteCompanyContext && pathMode === 'company');

        if (!shouldPersistFallbackUser) {
          resolvedUserData = true;
          setCurrentUserData(fallbackUserData);
          return;
        }

        try {
          await setDoc(
            doc(db, 'users', currentUser.uid),
            {
              ...fallbackUserData,
              createdAt: serverTimestamp(),
              updatedAt: serverTimestamp(),
            },
            { merge: true }
          );
          resolvedUserData = true;
          setCurrentUserData(fallbackUserData);
        } catch (error) {
          resolvedUserData = true;
          reportFirestoreError('users/createCurrentUser', error);
          setCurrentUserData(fallbackUserData);
        }
      },
      (error) => {
        resolvedUserData = true;
        reportFirestoreError('userDocSub', error);
        setCurrentUserData(fallbackUserData);
        setIsUserDataLoading(false);
      }
    );

    return () => {
      resolvedUserData = true;
      clearTimeout(loadingGuard);
      userDocUnsub();
    };
  }, [
    companyAccessState.resolvedCompanyId,
    companyAccessState.resolvedCompanyName,
    companyId,
    companyName,
    currentUser,
    directCompanyAccessKey,
    isDirectCompanyAccessResolved,
    pathMode,
    reportFirestoreError,
    shouldResolveDirectCompanyAccess,
  ]);

  useEffect(() => {
    if (!showTechnicalSettingsModal) return undefined;

    let isCancelled = false;
    getDocs(collection(db, 'users'))
      .then((snapshot) => {
        if (isCancelled) return;
        setAllUsers(snapshot.docs.map((docItem) => ({ uid: docItem.id, ...docItem.data() })));
      })
      .catch((error) => {
        if (isCancelled) return;
        setAllUsers([]);
        reportFirestoreError('allUsers/getDocs', error);
      });

    return () => {
      isCancelled = true;
    };
  }, [reportFirestoreError, showTechnicalSettingsModal]);

  const createNotification = useCallback(async (messageText, type, recipientId = null) => {
    if (!db || !appId) return;

    try {
      const notificationData = {
        message: messageText,
        type,
        timestamp: serverTimestamp(),
        readBy: [],
        companyId,
      };

      if (recipientId) notificationData.recipientId = recipientId;

      await addDoc(collection(db, `artifacts/${appId}/notifications`), notificationData);
    } catch (error) {
      reportFirestoreError('createNotification', error);
    }
  }, [appId, companyId, reportFirestoreError]);

  useEffect(() => {
    const nextStatus = companyCommercialState?.status || '';
    if (!nextStatus || lastCommercialStatusRef.current === nextStatus) return;

    lastCommercialStatusRef.current = nextStatus;

    if (companyCommercialState?.shouldRestrictOperations) {
      setSelectedTransport(null);
      setLogisticsRouteIntent(null);
      setDashboardPlansOpen(true);
      if (view !== 'menu') setView('menu');
      showMessage(companyCommercialState.lockedMessage || 'La operación quedó pausada por estado comercial.', 'error', 6500);
      return;
    }

    if (companyCommercialState?.isExpiringSoon) {
      setDashboardPlansOpen(true);
      showMessage(companyCommercialState.helperText || 'La vigencia comercial está próxima a vencer.', 'info', 5500);
    }
  }, [companyCommercialState, showMessage, view]);

  useEffect(() => {
    const moduleKey = moduleKeysByView[view];
    if (view === 'menu' || view === 'architect' || !moduleKey) return;

    if (companyCommercialState?.shouldRestrictOperations) {
      showMessage(companyCommercialState.lockedMessage || 'La operación quedó pausada por estado comercial.', 'error', 6000);
      setSelectedTransport(null);
      setLogisticsRouteIntent(null);
      setDashboardPlansOpen(true);
      setView('menu');
      return;
    }

    if (resolvedCompanyModules?.[moduleKey]?.enabled === false) {
      showMessage(`${moduleLabelsByView[view] || 'Este módulo'} está deshabilitado para ${companyName}.`, 'error');
      setSelectedTransport(null);
      setView('menu');
    }
  }, [companyCommercialState, resolvedCompanyModules, companyName, moduleKeysByView, moduleLabelsByView, showMessage, view]);

  const handleCompanyDeleted = useCallback((deletedCompanyId) => {
    if (!deletedCompanyId) return;

    if (companyId === deletedCompanyId) {
      setCompanyId(DEFAULT_COMPANY_ID);
      setCompanyName(DEFAULT_COMPANY_NAME);
      setSelectedTransport(null);
      setLogisticsRouteIntent(null);
      localStorage.setItem('activeCompanyId', DEFAULT_COMPANY_ID);
    }

    if (view !== 'architect') {
      setView('architect');
    }
  }, [companyId, view]);


  const handleNavigate = (newView) => {
    if (newView === view || isAnimating) return;

    if (newView === 'architect' && !canAccessArchitectPanel) {
      showMessage('No tenés permisos para abrir el panel del arquitecto.', 'error');
      return;
    }

    if (companyCommercialState?.shouldRestrictOperations && newView !== 'menu') {
      setSelectedTransport(null);
      setLogisticsRouteIntent(null);
      setDashboardPlansOpen(true);
      showMessage(companyCommercialState.lockedMessage || 'La operación quedó pausada por estado comercial.', 'error', 6000);
      if (view !== 'menu') setView('menu');
      return;
    }

    const moduleKey = moduleKeysByView[newView];
    if (moduleKey && resolvedCompanyModules?.[moduleKey]?.enabled === false) {
      showMessage(`${moduleLabelsByView[newView] || 'Este módulo'} está deshabilitado para ${companyName}.`, 'error');
      return;
    }

    if (newView !== 'logistics') {
      setSelectedTransport(null);
      setLogisticsRouteIntent(null);
    }

    if (newView !== 'menu') {
      setDashboardPlansOpen(false);
    }

    setIsAnimating(true);
    setAnimationClass('animate-slide-out-up');

    setTimeout(() => {
      setView(newView);
      setAnimationClass('animate-slide-in-down');
      setTimeout(() => {
        setIsAnimating(false);
        setAnimationClass('');
      }, 300);
    }, 300);
  };

  const giaPanelActions = useMemo(() => {
    const actions = [];

    if (view !== 'systemGuide') {
      actions.push({
        id: 'guide',
        label: 'Guía y soporte',
        tone: 'primary',
        onClick: () => {
          setGiaPanelOpen(false);
          handleNavigate('systemGuide');
        },
      });
    }

    if (view === 'menu' || view === 'monitor' || view === 'reports' || view === 'fleetExpenses') {
      actions.push({
        id: 'go-logistics',
        label: 'Ir a Logística',
        tone: 'secondary',
        onClick: () => {
          setGiaPanelOpen(false);
          handleNavigate('logistics');
        },
      });
    }

    if (view === 'reports') {
      actions.push({
        id: 'go-stock',
        label: 'Ir a Stock y movimientos',
        tone: 'secondary',
        onClick: () => {
          setGiaPanelOpen(false);
          handleNavigate('stock');
        },
      });
    }

    if (view === 'logistics' || view === 'service' || view === 'stock' || view === 'fleetExpenses') {
      actions.push({
        id: 'go-monitor',
        label: 'Ver Monitor en tiempo real',
        tone: 'secondary',
        onClick: () => {
          setGiaPanelOpen(false);
          handleNavigate('monitor');
        },
      });
    }

    return actions.filter((action, index, list) => list.findIndex((item) => item.id === action.id) === index);
  }, [view]);

  const handleOpenLogisticsRouteFromMonitor = (route, destination = null) => {
    if (!route?.id) return;

    const nextCompanyId = route.companyId || companyId || DEFAULT_COMPANY_ID;
    const nextCompanyName =
      companyOptions.find((option) => option.id === nextCompanyId)?.name ||
      route.companyName ||
      nextCompanyId;

    if (nextCompanyId !== companyId) {
      setCompanyId(nextCompanyId);
      setCompanyName(nextCompanyName);
    }

    setSelectedTransport({
      id: route.transporterId || route.transporterSlug || route.transportType || route.transport || route.id,
      slug: route.transporterSlug || '',
      transportType: route.transportType || route.transport || '',
      name: route.transporterName || route.driverName || route.choferName || route.transportType || route.transport || 'Transporte',
      companyId: nextCompanyId,
      companyName: nextCompanyName,
    });

    setLogisticsRouteIntent({
      routeId: route.id,
      destinationId: destination?.id || null,
      companyId: nextCompanyId,
    });

    handleNavigate('logistics');
  };

  const handleLogout = async () => {
    try {
      if (currentUser?.uid) localStorage.removeItem(`admin_pass_verified_${currentUser.uid}`);
      setIsAuthenticatedWithPassword(false);
      await signOut(auth);
    } catch (error) {
      console.error('Error al cerrar sesión:', error);
      showMessage('Error al cerrar sesión.', 'error');
    }
  };

  const handleCompanyChange = (event) => {
    const nextCompanyId = event.target.value;
    if (!nextCompanyId || nextCompanyId === companyId) return;

    const nextCompanyName = companyOptions.find((c) => c.id === nextCompanyId)?.name || nextCompanyId;
    setCompanyId(nextCompanyId);
    setSelectedTransport(null);
    showMessage(isArchitectView ? `Contexto preparado para ${nextCompanyName}.` : `Empresa activa: ${nextCompanyName}`, 'success');
  };

  const handleGoToPublicHome = useCallback(() => navigateToPath('/'), [navigateToPath]);
  const handleGoToLogin = useCallback(async () => {
    if (auth.currentUser) {
      try {
        await signOut(auth);
      } catch (error) {
        console.error('No se pudo limpiar la sesión antes de ingresar:', error);
      }
    }

    navigateToPath('/ingresar');
  }, [navigateToPath]);
  const handleGoToTrial = useCallback(() => navigateToPath('/probar-gratis'), [navigateToPath]);
  const handleGoToCommercialRequest = useCallback((planKey = 'base') => {
    const normalizedPlanKey = String(planKey || 'base').trim().toLowerCase() || 'base';
    setPublicPlanInterest(normalizedPlanKey);
    if (typeof window !== 'undefined') {
      window.localStorage.setItem('gcentra_public_plan_interest', normalizedPlanKey);
    }
    navigateToPath('/solicitar-plan');
  }, [navigateToPath]);
  const handleOpenArchitectPanel = useCallback(() => {
    navigateToPath('/control');
  }, [navigateToPath]);
  const handleGoToSystemWorkspace = useCallback(() => {
    navigateToPath('/ingresar');
  }, [navigateToPath]);

useEffect(() => {
  if (!currentUser || isUserDataLoading || pathMode !== 'login') return;

  if (canAccessArchitectPanel) {
    navigateToPath('/control');
    return;
  }

  if (primaryCompanyAccessPath) {
    navigateToPath(primaryCompanyAccessPath);
  }
}, [canAccessArchitectPanel, currentUser, isUserDataLoading, navigateToPath, pathMode, primaryCompanyAccessPath]);

  const transporterMatch = currentPathname.match(/\/(?:empresa\/)?transporter\/([^/]+)/);

  if (transporterMatch) {
    const transporterSlug = decodeURIComponent(transporterMatch[1]);
    return <TransporterLogisticsApp showMessage={showMessage} transporterSlug={transporterSlug} companyId={null} companyName={null} />;
  }

  const retryDirectCompanyAccess = () => {
    if (!shouldResolveDirectCompanyAccess) return;
    setCompanyAccessState((prev) => ({
      ...prev,
      status: 'loading',
      message: '',
    }));
    setCompanyAccessRetryNonce((prev) => prev + 1);
  };


if (showSplash || !isAuthReady || isDirectCompanyAccessPending || (currentUser && !currentUserData && isUserDataLoading)) {
  return (
    <SplashScreen
      title="G-CENTRA"
      secondaryTitle={isDirectCompanyAccess ? (companyAccessState.resolvedCompanyName || prettifyAccessLabel(directCompanyAccessKey) || 'Tu empresa') : 'El centro de tu logística, en tiempo real'}
      description={isDirectCompanyAccess ? 'Preparando tu espacio de trabajo.' : 'Monitoreá rutas, transportistas y entregas desde una sola plataforma.'}
    />
  );
}
if (firebaseInitError) return <ErrorScreen error={firebaseInitError} />;

if (pathMode === 'public' || pathMode === 'plans') {
  return (
    <PublicLanding
      onGoToLogin={handleGoToLogin}
      onGoToTrial={handleGoToTrial}
      onGoToCommercialRequest={handleGoToCommercialRequest}
    />
  );
}

  if (pathMode === 'trial') {
    return <TrialSignupPage onBackHome={handleGoToPublicHome} onGoToLogin={handleGoToLogin} onComplete={navigateToPath} />;
  }

  if (pathMode === 'stock-controller' && stockControllerAccess) {
    return (
      <StockControllerAccessPage
        companyAccessKey={stockControllerAccess.companyAccessKey}
        controllerAccessKey={stockControllerAccess.controllerAccessKey}
        onBackHome={handleGoToPublicHome}
      />
    );
  }

if (pathMode === 'commercial-request') {
  return (
    <DemoRequestPage
      onBackHome={handleGoToPublicHome}
      onGoToLogin={handleGoToLogin}
      requestedPlan={publicPlanInterest}
    />
  );
}

if (isDirectCompanyAccessBlocked) {
  if (companyAccessState.status === 'inactive') {
    return (
      <CompanyAccessStatusScreen
        tone="amber"
        badge="Empresa inactiva"
        title={companyAccessState.resolvedCompanyName || 'Acceso temporalmente no disponible'}
        description={companyAccessState.message || 'La empresa existe, pero su acceso no está operativo en este momento.'}
        primaryAction={{ label: 'Volver al inicio', onClick: handleGoToPublicHome }}
        secondaryAction={currentUser ? { label: 'Cerrar sesión', onClick: handleLogout } : null}
      />
    );
  }

  if (companyAccessState.status === 'not-found') {
    return (
      <CompanyAccessStatusScreen
        tone="red"
        badge="Empresa no encontrada"
        title="No encontramos este acceso"
        description={companyAccessState.message || 'Revisá el enlace o pedile a la empresa que te comparta el acceso correcto.'}
        primaryAction={{ label: 'Volver al inicio', onClick: handleGoToPublicHome }}
        secondaryAction={currentUser ? { label: 'Cerrar sesión', onClick: handleLogout } : null}
      />
    );
  }

  return (
    <CompanyAccessStatusScreen
      tone="red"
      badge="No pudimos abrir este acceso"
      title="Intentemos de nuevo"
      description={companyAccessState.message || 'Hubo un problema al intentar abrir esta empresa.'}
      primaryAction={{ label: 'Reintentar', onClick: retryDirectCompanyAccess }}
      secondaryAction={{ label: 'Volver al inicio', onClick: handleGoToPublicHome }}
    />
  );
}

if (pathMode === 'control' && !currentUser) {
  return (
    <div onClick={() => playSound('start')}>
      <Auth
        showMessage={showMessage}
        title="Control G-CENTRA"
        subtitle="Ingresá con la cuenta interna de G-CENTRA para administrar empresas, accesos y configuraciones del sistema."
        allowRegister={false}
        allowGoogle
        allowNewGoogleUsers
        contextLabel="Backoffice interno"
        backLabel="Volver al inicio"
        onBack={handleGoToPublicHome}
      />
    </div>
  );
}

if (pathMode === 'control' && !canAccessArchitectPanel) {
  return (
    <CompanyAccessStatusScreen
      tone="red"
      badge="Acceso restringido"
      title="No tenés permisos para Arquitectura"
      description="Este panel es solo para accesos internos de arquitectura."
      primaryAction={{ label: 'Ir al sistema', onClick: handleGoToSystemWorkspace }}
      secondaryAction={{ label: 'Cerrar sesión', onClick: handleLogout }}
    />
  );
}

if (pathMode === 'control') {
  return (
    <div className="min-h-screen bg-slate-100">
      <ArchitectPanel
        currentUserData={currentUserData || { role: 'architect', companyName: 'Arquitectura' }}
        companyOptions={companyOptions}
        activeCompanyId={companyId}
        onCompanyDeleted={handleCompanyDeleted}
        showMessage={showMessage}
      />
    </div>
  );
}

if (!currentUser) {
  if (pathMode === 'company') {
    return (
      <div onClick={() => playSound('start')}>
        <Auth
          showMessage={showMessage}
          title={companyAccessState.resolvedCompanyName || prettifyAccessLabel(directCompanyAccessKey) || 'Tu empresa'}
          subtitle="Ingresá a tu espacio de trabajo."
          allowRegister={false}
          allowGoogle={true}
          backLabel="Volver al inicio"
          onBack={handleGoToPublicHome}
        />
      </div>
    );
  }

  return (
    <div onClick={() => playSound('start')}>
      <Auth
        showMessage={showMessage}
        title="Ingresar"
        subtitle="Entrá con tu cuenta y continuá con tu operación."
        allowRegister={false}
        allowGoogle={true}
        backLabel="Volver al inicio"
        onBack={handleGoToPublicHome}
      />
    </div>
  );
}

  if (currentUser && !isUserDataLoading && currentUserData?.adminPassword && !isAuthenticatedWithPassword) {
    return (
      <PasswordVerificationModal
        show
        user={currentUser}
        mode="admin"
        adminPassword={currentUserData.adminPassword}
        onVerifySuccess={() => setIsAuthenticatedWithPassword(true)}
        onClose={handleLogout}
        showMessage={showMessage}
      />
    );
  }

  const renderView = () => {
    if (view === 'logistics' && selectedTransport) {
      return (
        <LogisticsApp
          userId={currentUser.uid}
          userRole={currentUserData?.role}
          showMessage={showMessage}
          addLogisticsNotification={(msg) => createNotification(msg, 'logistics')}
          selectedTransport={selectedTransport}
          onBackToDashboard={() => {
            setSelectedTransport(null);
            setLogisticsRouteIntent(null);
          }}
          initialRouteIntent={logisticsRouteIntent}
          onConsumeInitialRouteIntent={() => setLogisticsRouteIntent(null)}
          companyId={companyId}
          companyName={companyName}
          companyModules={resolvedCompanyModules}
          companyPlanSettings={companyPlanSettings}
          commercialState={companyCommercialState}
          onGiaContextChange={handleGiaContextChange}
          onStartRouteCreatorTutorial={startRouteCreatorTutorial}
          routeCreatorTutorialSeen={routeCreatorTutorialSeen}
          tutorialActive={tutorialRun && currentTutorialId === 'routeCreator'}
          tutorialFocusTarget={currentTutorialId === 'routeCreator' ? currentTutorialTarget : null}
          />
      );
    }

    switch (view) {
      case 'stock':
        return <StockControlApp userId={currentUser.uid} userRole={currentUserData?.role} showMessage={showMessage} onBackToMenu={() => handleNavigate('menu')} addStockNotification={(msg) => createNotification(msg, 'stock')} playSound={playSound} companyId={companyId} companyName={companyName} onGiaContextChange={handleGiaContextChange} onStartTutorial={startStockTutorial} tutorialSeen={stockTutorialSeen} tutorialActive={tutorialRun && currentTutorialId === 'stock'} tutorialFocusTarget={currentTutorialId === 'stock' ? currentTutorialTarget : null} />;
      case 'logistics':
        return <LogisticsDashboard onSelectTransport={(transport) => setSelectedTransport(transport)} onBackToMenu={() => handleNavigate('menu')} onStartTutorial={startLogisticsTutorial} tutorialSeen={logisticsTutorialSeen} tutorialActive={tutorialRun && currentTutorialId === 'logistics'} tutorialFocusTarget={currentTutorialId === 'logistics' ? currentTutorialTarget : null} onStartUnitModalTutorial={startUnitModalTutorial} unitModalTutorialSeen={unitModalTutorialSeen} unitModalTutorialRepeatEnabled={unitModalTutorialRepeatEnabled} onToggleUnitModalTutorialRepeat={toggleUnitModalTutorialRepeat} routes={logisticsRoutes} showMessage={showMessage} companyId={companyId} companyName={companyName} companyModules={resolvedCompanyModules} companyPlanSettings={companyPlanSettings} commercialState={companyCommercialState} tutorialTransitionRequest={queuedTutorialId} onGiaContextChange={handleGiaContextChange} />;
      case 'service':
        return <ServiceApp onBackToMenu={() => handleNavigate('menu')} onGoToLogistics={() => handleNavigate('logistics')} onStartTutorial={startServiceTutorial} tutorialSeen={serviceTutorialSeen} tutorialActive={tutorialRun && currentTutorialId === 'service'} tutorialFocusTarget={currentTutorialId === 'service' ? currentTutorialTarget : null} showMessage={showMessage} companyId={companyId} companyName={companyName} onGiaContextChange={handleGiaContextChange} />;
      case 'monitor':
        return <RouteMonitor onBackToMenu={() => handleNavigate('menu')} showMessage={showMessage} companyId={companyId} companyName={companyName} mode="monitor" initialTab="live" allowedTabs={['live']} onOpenRouteToLogistics={handleOpenLogisticsRouteFromMonitor} onStartTutorial={startMonitorTutorial} tutorialSeen={monitorTutorialSeen} tutorialActive={tutorialRun && currentTutorialId === 'monitor'} tutorialFocusTarget={currentTutorialId === 'monitor' ? currentTutorialTarget : null} onMonitorTutorialContextChange={({ hasActiveRoutes }) => setMonitorTutorialHasActiveRoutes(hasActiveRoutes)} onGiaContextChange={handleGiaContextChange} onGoToLogistics={() => handleNavigate('logistics')} />;
        case 'reports':
          return <Reports onBackToMenu={() => handleNavigate('menu')} showMessage={showMessage} companyId={companyId} companyName={companyName} onOpenRouteToLogistics={handleOpenLogisticsRouteFromMonitor} onGiaContextChange={handleGiaContextChange} onStartTutorial={startReportsTutorial} tutorialSeen={reportsTutorialSeen} tutorialActive={tutorialRun && currentTutorialId === 'reports'} tutorialFocusTarget={currentTutorialId === 'reports' ? currentTutorialTarget : null} />;
      case 'fleetExpenses':
          return <FleetExpensesApp onBackToMenu={() => handleNavigate('menu')} showMessage={showMessage} companyId={companyId} companyName={companyName} onGiaContextChange={handleGiaContextChange} onStartTutorial={startFleetExpensesTutorial} tutorialSeen={fleetExpensesTutorialSeen} tutorialActive={tutorialRun && currentTutorialId === 'fleetExpenses'} tutorialFocusTarget={currentTutorialId === 'fleetExpenses' ? currentTutorialTarget : null} />;
      case 'systemGuide':
        return (
          <SystemGuide
            onBackToMenu={() => handleNavigate('menu')}
            companyId={companyId}
            companyName={companyName}
            companyModules={resolvedCompanyModules}
            companyGeneralSettings={companyGeneralSettings}
            companyPlanSettings={companyPlanSettings}
            commercialState={companyCommercialState}
          />
        );
      case 'database':
        return (
          <OperationalDatabaseView
            companyId={companyId}
            companyName={companyName}
            showMessage={showMessage}
            onBackToMenu={() => handleNavigate('menu')}
          />
        );
      case 'architect':
        return <ArchitectPanel currentUserData={currentUserData} companyOptions={companyOptions} activeCompanyId={companyId} onCompanyDeleted={handleCompanyDeleted} showMessage={showMessage} />;
      case 'menu':
      default:
        return (
          <Dashboard
    onNavigate={handleNavigate}
    onStartTutorial={startDashboardTutorial}
    tutorialSeen={tutorialSeen}
    currentUserData={currentUserData}
    companyId={companyId}
    companyName={companyName}
    companyModules={resolvedCompanyModules}
    companyPlanSettings={companyPlanSettings}
    commercialState={companyCommercialState}
    canAccessArchitectPanel={canAccessArchitectPanel}
    showPlansPanel={dashboardPlansOpen}
    onTogglePlansPanel={handleHeaderPlansClick}
    showMessage={showMessage}
  />
);
    }
  };

  return (
    <TutorialProvider value={tutorial}>
      <div className="flex min-h-screen flex-col bg-slate-100">
        <TutorialOverlay {...tutorialOverlayProps} />

        <header className="header-sweep sticky top-0 z-40 h-[76px] overflow-hidden border-b border-slate-700/50 bg-slate-950 px-4 text-white shadow-lg md:px-6">
        <HeaderAnimation />
        <div className="pointer-events-none absolute inset-0">
          <img
            src={gcentraHeaderMap}
            alt=""
            className="h-full w-full object-cover opacity-[0.48] saturate-[0.98]"
          />
          <div className="absolute inset-0 bg-[radial-gradient(circle_at_left,rgba(56,189,248,0.18),transparent_26%),linear-gradient(90deg,rgba(2,6,23,0.97)_0%,rgba(2,6,23,0.74)_24%,rgba(2,6,23,0.46)_42%,rgba(2,6,23,0.8)_100%)]" />
          <div className="absolute inset-y-0 left-0 w-[24rem] bg-[linear-gradient(90deg,rgba(2,6,23,0.78)_0%,rgba(2,6,23,0.54)_38%,transparent_100%)]" />
          <div className="absolute left-[3.35rem] top-1/2 hidden h-px w-52 -translate-y-1/2 bg-gradient-to-r from-cyan-100/22 via-cyan-300/10 to-transparent md:block" />
          <div className="absolute left-[3.35rem] top-1/2 hidden h-8 w-52 -translate-y-1/2 bg-[radial-gradient(circle_at_left,rgba(103,232,249,0.12),transparent_68%)] md:block" />
          <div className="absolute inset-y-0 right-0 hidden w-[28rem] bg-[linear-gradient(90deg,rgba(2,6,23,0)_0%,rgba(2,6,23,0.28)_26%,rgba(2,6,23,0.82)_100%)] xl:block" />
        </div>

        <div id="company-header" className="relative z-10 flex h-full items-center justify-between gap-4 overflow-hidden">
            <div className="min-w-0 flex-1">
            <div className="flex flex-wrap items-center gap-3 md:flex-nowrap">
              <div className="relative hidden h-full w-[52px] shrink-0 md:block">
                <img
                  src={gcentraMarkCutout}
                  alt=""
                  className="absolute left-0 top-1/2 h-[54px] w-[54px] -translate-y-1/2 object-contain opacity-[0.74]"
                />
              </div>
              <div className="min-w-0 self-center">
                <div className="flex flex-wrap items-center gap-2">
                  <span className="text-sm font-black uppercase leading-none tracking-[0.23em] text-cyan-100 drop-shadow-[0_0_12px_rgba(103,232,249,0.18)]">G-CENTRA</span>
                  <span className="rounded-full border border-white/10 bg-slate-900/70 px-2 py-0.5 text-[11px] font-semibold text-slate-300 backdrop-blur">v{packageJson.version}</span>
                </div>
                <p className="pt-0.5 text-[10px] font-semibold uppercase leading-none tracking-[0.18em] text-slate-400">
                  Núcleo logístico inteligente
                </p>
              </div>

              <div className="hidden h-8 w-px bg-white/10 sm:block" />
              <div className="min-w-0 self-center">
                <p className="truncate text-sm font-bold leading-tight text-white sm:text-base">{isArchitectView ? 'Arquitectura' : companyName}</p>
                <p className="pt-0.5 hidden truncate text-[11px] leading-none text-slate-300 md:block">
                  {isArchitectView ? 'Entorno ejecutivo' : 'Entorno de trabajo'}
                </p>
              </div>
            </div>
          </div>

          <div className="hidden flex-1 items-center justify-center xl:flex">
            <div className="flex flex-wrap items-center justify-center gap-2">
              {!isArchitectView ? <CompactInfoPill tone="violet" iconPath="M3.75 5.25h16.5M3.75 12h16.5M3.75 18.75h16.5" text={`${enabledModulesCount} módulos`} /> : null}
              {!isArchitectView ? <CompactInfoPill tone={companyPlanHeaderTone} iconPath="M2.25 8.25h19.5M2.25 9h19.5m-16.5 5.25h6m-6 2.25h6m3-3.75l-3.75-3.75A1.125 1.125 0 015.25 6H3.75a2.25 2.25 0 00-2.25 2.25v1.5a2.25 2.25 0 002.25 2.25h1.5a1.125 1.125 0 01.83.375l3.75 3.75a1.125 1.125 0 010 1.59l-3.75 3.75a1.125 1.125 0 01-.83.375H3.75a2.25 2.25 0 01-2.25-2.25v-1.5a2.25 2.25 0 012.25-2.25h1.5A1.125 1.125 0 005.25 12z" text={companyPlanHeaderLabel} /> : null}
            </div>
          </div>

          <div className="flex flex-1 items-center justify-end gap-2">
            <button
              type="button"
              onClick={() => setGiaPanelOpen((prev) => !prev)}
              className="group hidden min-w-0 max-w-[210px] items-center self-center px-2 py-1 text-left transition-all xl:flex"
              title="Abrir asistencia contextual de GIA"
            >
              <div className="min-w-0 text-left">
                <p className="truncate text-[11px] font-semibold uppercase tracking-[0.16em] text-slate-300 group-hover:text-white">
                  <span className="font-black text-emerald-300">GIA Soporte</span>
                  <span className="mx-2 text-slate-500">/</span>
                  <span>{giaSectionLabel}</span>
                </p>
              </div>
            </button>

            <button
              type="button"
              onClick={() => setGiaPanelOpen((prev) => !prev)}
              className="rounded-xl bg-emerald-500/10 px-3 py-2 text-xs font-semibold text-emerald-100 transition-all hover:bg-emerald-500/20 xl:hidden"
              title="Abrir asistencia contextual de GIA"
            >
              GIA Soporte
            </button>

            {!isArchitectView && !isDirectCompanyAccess ? (
              <select
                value={companyId}
                onChange={handleCompanyChange}
                className="hidden rounded-xl border border-slate-700 bg-slate-900 px-3 py-2 text-xs font-semibold text-slate-200 outline-none transition-all focus:ring-2 focus:ring-cyan-400 md:block"
                title="Empresa activa"
              >
                {companyOptions.map((option) => (
                  <option key={option.id} value={option.id}>{option.name}</option>
                ))}
              </select>
            ) : null}

            {canAccessArchitectPanel && !isArchitectView && !isDirectCompanyAccess ? (
              <button type="button" onClick={handleOpenArchitectPanel} className="rounded-xl border border-cyan-400/30 bg-cyan-500/10 px-3 py-2 text-xs font-semibold text-cyan-100 transition-all hover:bg-cyan-500/20">
                Arquitectura
              </button>
            ) : null}

            {isArchitectView ? (
              <button type="button" onClick={handleGoToSystemWorkspace} className="rounded-xl border border-cyan-400/30 bg-cyan-500/10 px-3 py-2 text-xs font-semibold text-cyan-100 transition-all hover:bg-cyan-500/20">
                Sistema
              </button>
            ) : null}
            {!isArchitectView ? (
              <div className="hidden lg:block xl:hidden">
                <CompactInfoPill tone={companyPlanHeaderTone} iconPath="M2.25 8.25h19.5M2.25 9h19.5m-16.5 5.25h6m-6 2.25h6m3-3.75l-3.75-3.75A1.125 1.125 0 015.25 6H3.75a2.25 2.25 0 00-2.25 2.25v1.5a2.25 2.25 0 002.25 2.25h1.5a1.125 1.125 0 01.83.375l3.75 3.75a1.125 1.125 0 010 1.59l-3.75 3.75a1.125 1.125 0 01-.83.375H3.75a2.25 2.25 0 01-2.25-2.25v-1.5a2.25 2.25 0 012.25-2.25h1.5A1.125 1.125 0 005.25 12z" text={companyPlanHeaderLabel} />
              </div>
            ) : null}
{!isArchitectView ? (
  <button
    type="button"
    onClick={handleHeaderPlansClick}
    className="ml-1 rounded-xl border border-cyan-400/30 bg-cyan-500/10 px-3 py-2 text-xs font-semibold text-cyan-100 transition-all hover:bg-cyan-500/20"
  >
    {view === 'menu' && dashboardPlansOpen ? 'Ocultar planes' : 'Ver planes'}
  </button>
) : null}
            <button
              type="button"
              onClick={() => (isArchitectView ? setShowTechnicalSettingsModal(true) : setShowCompanySettingsModal(true))}
              className="rounded-xl border border-slate-700 bg-slate-900 p-2 text-slate-300 transition-colors hover:text-white"
              title={isArchitectView ? 'Ajustes técnicos' : 'Configuración de empresa'}
            >
              <Icon path="M9.594 3.94c.09-.542.56-1.008 1.11-1.233.554-.225 1.157-.225 1.712 0 .553.225 1.02.69 1.11 1.233l.098.582a2.25 2.25 0 001.122 1.664l.536.382c.513.366.865.936.936 1.551.07.615-.157 1.225-.638 1.664l-.382.536a2.25 2.25 0 00-1.664 1.122l-.582.098c-.542.09-.69.56-1.233 1.11-.225.554-.225 1.157 0 1.712.225.553.69 1.02 1.233 1.11l.582.098a2.25 2.25 0 001.664 1.122l.536.382c.439.481 1.049.708 1.664.638.615-.071 1.185-.423 1.551-.936l.382-.536a2.25 2.25 0 001.122-1.664l.098-.582c.09-.542.56-1.008 1.11-1.233.554-.225 1.157-.225 1.712 0 .553.225 1.02.69 1.11 1.233" className="h-5 w-5" />
            </button>

            {canAccessArchitectPanel && !isArchitectView ? (
              <button
                type="button"
                onClick={() => setShowTechnicalSettingsModal(true)}
                className="hidden rounded-xl border border-slate-700 bg-slate-900 px-3 py-2 text-xs font-semibold text-slate-300 transition-colors hover:text-white lg:inline-flex"
                title="Ajustes técnicos"
              >
                Ajustes
              </button>
            ) : null}

            <button type="button" onClick={handleLogout} className="rounded-xl border border-slate-700 bg-slate-900 p-2 text-slate-300 transition-colors hover:text-white" title="Cerrar sesión">
              <Icon path="M15.75 9V5.25A2.25 2.25 0 0013.5 3h-6a2.25 2.25 0 00-2.25 2.25v13.5A2.25 2.25 0 007.5 21h6a2.25 2.25 0 002.25-2.25V15M12 9l-3 3m0 0l3 3m-3-3h12.75" className="h-5 w-5" />
            </button>
          </div>
        </div>
      </header>

      {message.text ? (
        <div className={`fixed right-4 top-28 z-[120] rounded-lg px-4 py-3 font-semibold text-white shadow-lg ${message.type === 'error' ? 'bg-red-500' : message.type === 'success' ? 'bg-green-500' : 'bg-slate-800'}`}>
          {message.text}
        </div>
      ) : null}

      <GiaAssistantPanel
        open={giaPanelOpen}
        onClose={() => setGiaPanelOpen(false)}
        contextPacket={giaContextPacket}
        quickAnswers={giaQuickAnswers}
        flowKnowledge={giaFlowKnowledge}
        actions={giaPanelActions}
        onOpenGuide={() => {
          setGiaPanelOpen(false);
          handleNavigate('systemGuide');
        }}
      />

      <QuickNavSidebar show={view !== 'menu' && view !== 'database' && !isArchitectView} activeView={view} onNavigate={handleNavigate} enabledViews={enabledQuickNavViews} />

      <CompanySettingsModal
        show={showCompanySettingsModal}
        onClose={() => setShowCompanySettingsModal(false)}
        companyId={companyId}
        companyName={companyName}
        showMessage={showMessage}
      />

      <SettingsModal
        show={showTechnicalSettingsModal}
        onClose={() => setShowTechnicalSettingsModal(false)}
        soundEnabled={soundEnabled}
        onToggleSound={() => setSoundEnabled((prev) => !prev)}
        showMessage={showMessage}
        allUsers={allUsers}
        currentUserData={currentUserData}
      />

      <main className={`relative flex-grow overflow-hidden transition-all duration-500 ${view !== 'menu' && !isArchitectView ? 'md:pl-28' : ''}`}>
        <div className={`h-full w-full ${animationClass}`}>{renderView()}</div>
      </main>
      </div>
    </TutorialProvider>
  );
}

export default App;
